import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import OrderMobileCardShell from "../../Order/OrderMobileCardShell";
import AuthService from "../../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../../customComponents/CustomAlertMessage";
import "../../FundingAccountPage/fundingAccount.css";
import CrossMarginRiskModal, { parseCrossRisk } from "../CrossMarginRiskModal";
import CrossMarginLevelGauge from "../CrossMarginLevelGauge";
import ThemeAwareSpinner from "../../../../customComponents/ThemeAwareSpinner";

function CustomFilterDropdown({ options, value, onChange, label }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value) || options[0];

    return (
        <div className="agce-custom-select-container" ref={containerRef}>
            <div 
                className="agce-custom-select-trigger" 
                onClick={() => setIsOpen(!isOpen)}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span>{selectedOption ? selectedOption.label : label}</span>
                <i className={`ri-arrow-down-s-line ${isOpen ? 'open' : ''}`}></i>
            </div>
            {isOpen && (
                <ul className="agce-custom-select-options" role="listbox">
                    {options.map((o) => (
                        <li 
                            key={o.value || "all"} 
                            className={`agce-custom-select-option ${o.value === value ? 'selected' : ''}`}
                            onClick={() => {
                                onChange(o.value);
                                setIsOpen(false);
                            }}
                            role="option"
                            aria-selected={o.value === value}
                        >
                            {o.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function MarginDateInput({ value, onChange, placeholder = "From", className = "", ...props }) {
    return (
        <div className="agce-margin-date-input-wrap" data-placeholder={placeholder} data-empty={!value}>
            <input
                type="date"
                value={value}
                onChange={onChange}
                className={`agce-margin-filter-input ${className}`.trim()}
                {...props}
            />
        </div>
    );
}

/** Open Orders — horizontal chip row with left/right arrows on small screens (matches OrdersSubTabs pattern). */
function SpotOpenOrdersFilterRow({
    SPOT_OPEN_ORDER_KINDS,
    openOrderKindTab,
    setOpenOrderKindTab,
    orderType,
    setorderType,
}) {
    const scrollerRef = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateArrows = useMemo(
        () => () => {
            const el = scrollerRef.current;
            if (!el) return;
            const max = el.scrollWidth - el.clientWidth;
            const left = el.scrollLeft;
            setCanLeft(left > 2);
            setCanRight(max > 2 && left < max - 2);
        },
        []
    );

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;
        updateArrows();
        el.addEventListener("scroll", updateArrows, { passive: true });
        window.addEventListener("resize", updateArrows);
        const t = window.setTimeout(updateArrows, 150);
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateArrows) : null;
        if (ro) ro.observe(el);
        return () => {
            window.clearTimeout(t);
            el.removeEventListener("scroll", updateArrows);
            window.removeEventListener("resize", updateArrows);
            if (ro) ro.disconnect();
        };
    }, [updateArrows, SPOT_OPEN_ORDER_KINDS.length, openOrderKindTab, orderType]);

    const scrollByAmount = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const amount = Math.max(160, Math.floor(el.clientWidth * 0.65));
        el.scrollBy({ left: dir * amount, behavior: "smooth" });
    };

    return (
        <div className="spot_orders_filter_bar">
            <div className="spot_orders_filter_scrollWrap">
                <button
                    type="button"
                    className={`spot_orders_chipsArrow spot_orders_chipsArrow--left${canLeft ? "" : " is-hidden"}`}
                    aria-label="Scroll filters left"
                    onClick={() => scrollByAmount(-1)}
                >
                    <i className="ri-arrow-left-s-line" aria-hidden="true" />
                </button>

                <div className="spot_orders_filter_chips spot_orders_filter_chips--scroll" ref={scrollerRef}>
                    {SPOT_OPEN_ORDER_KINDS.map((k) => (
                        <button
                            key={k.id}
                            type="button"
                            className={`spot_orders_chip ${openOrderKindTab === k.id ? "is-active" : ""}`}
                            onClick={() => setOpenOrderKindTab(k.id)}
                        >
                            {k.label}
                        </button>
                    ))}

                    <div className="spot_orders_filter_actions">
                        <CustomFilterDropdown
                            options={[
                                { value: "All", label: "All Sides" },
                                { value: "BUY", label: "Buy" },
                                { value: "SELL", label: "Sell" }
                            ]}
                            value={orderType}
                            onChange={(val) => setorderType(val)}
                            label="All Sides"
                        />
                        <button
                            type="button"
                            className="spot_orders_reset"
                            onClick={() => {
                                setOpenOrderKindTab("all");
                                setorderType("All");
                            }}
                        >
                            <i className="ri-refresh-line" aria-hidden="true" />
                            Reset
                        </button>
                    </div>
                </div>

                <button
                    type="button"
                    className={`spot_orders_chipsArrow spot_orders_chipsArrow--right${canRight ? "" : " is-hidden"}`}
                    aria-label="Scroll filters right"
                    onClick={() => scrollByAmount(1)}
                >
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}


function FilterBarScrollable({ children }) {
    const [isPopupOpen, setIsPopupOpen] = React.useState(false);

    return (
        <div className="agce-margin-filter-container">
            {/* Desktop view: standard inline bar */}
            <div className="desktop_view2">
                <div className="d-flex align-items-center w-100 agce-margin-filter-bar">
                    {children}
                </div>
            </div>

            {/* Mobile view: Filter toggle and popup */}
            <div className="agce-margin-mobile-filter-wrap">
                <div className="agce-margin-mobile-filter-header" style={{ padding: "10px 16px 0px" }}>
                    <button type="button" className="agce-margin-filter-toggle-btn" onClick={() => setIsPopupOpen(true)}>
                        <i className="ri-filter-3-line" /> Filter
                    </button>
                </div>

                {isPopupOpen && (
                    <div className="futures-margin-modal position-fixed w-100 h-100 top-0 start-0" style={{ zIndex: 9999, background: "rgba(0,0,0,0.5)" }} onClick={() => setIsPopupOpen(false)}>
                        <div className="cntnt bottom-margin-modal agce-margin-filter-popup position-absolute bottom-0 w-100" style={{ background: "var(--bg-primary, #fff)", borderTopLeftRadius: 16, borderTopRightRadius: 16 }} onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header border-0 pb-0 pt-3 px-4">
                                <h6>Filters</h6>
                                <button type="button" className="btn-close" onClick={() => setIsPopupOpen(false)} />
                            </div>
                            <div className="modal-body p-4 agce-margin-filter-popup-content">
                                {children}
                            </div>
                            <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex">
                                <button type="button" className="btn btn-primary w-100 agce-margin-filter-apply-btn" onClick={() => setIsPopupOpen(false)}>
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// Asset History tab  (Borrow / Repay / Interest / Transfer)
// ─────────────────────────────────────────────────────────
const AH_SUB_TABS = [
    { id: "borrow", label: "Borrow" },
    { id: "repay", label: "Repay" },
    { id: "interest", label: "Interest" },
    { id: "transfer", label: "Transfer" },
];
const AH_PRESET_LABELS = ["1D", "7D", "30D", "90D"];
const ahToLocalIso = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const ahToIsoFrom = (s) => s ? `${s}T00:00:00.000Z` : undefined;
const ahToIsoTo = (s) => s ? `${s}T23:59:59.999Z` : undefined;
const ahFmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

function AssetHistoryTab({ pairId, token }) {
    const [subTab, setSubTab] = useState("borrow");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [preset, setPreset] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const applyPreset = useCallback((p) => {
        const now = new Date();
        const days = p === "1D" ? 1 : p === "7D" ? 7 : p === "30D" ? 30 : 90;
        const start = new Date(now);
        start.setDate(now.getDate() - days);
        setStartDate(ahToLocalIso(start));
        setEndDate(ahToLocalIso(now));
        setPreset(p);
    }, []);

    const fetchRows = useCallback(async (tab, pid, from, to) => {
        if (!token) { setRows([]); return; }
        setLoading(true);
        try {
            const params = { pairId: pid || undefined, from: ahToIsoFrom(from), to: ahToIsoTo(to) };
            let res;
            if (tab === "borrow") res = await AuthService.marginBorrowHistory(params);
            else if (tab === "repay") res = await AuthService.marginRepayHistory(params);
            else if (tab === "interest") res = await AuthService.marginInterestHistory(params);
            else res = await AuthService.marginTransferHistory(params);
            if (res?.success) {
                const data = Array.isArray(res.data) ? res.data : [];
                setRows(data.map((r) => {
                    const base = {
                        id: r.id ?? r._id ?? r.time,
                        time: ahFmtDate(r.created_at ?? r.time ?? r.timestamp),
                        pair: r.pair ?? r.contract ?? "—",
                        coin: r.asset ?? r.coin ?? "—",
                        amount: r.amount ?? r.amount_charged ?? "—",
                        status: r.status ?? "Completed",
                    };
                    if (tab === "interest") {
                        base.loanAmount = r.loan_amount ?? "—";
                        base.hourlyRate = r.hourly_rate != null ? `${(r.hourly_rate * 100).toFixed(6)}%` : "—";
                        base.apr = r.apr != null ? `${(r.apr * 100).toFixed(4)}%` : "—";
                    }
                    if (tab === "transfer") {
                        base.transferType = r.type ?? "—";
                        base.from = r.from ?? "—";
                        base.to = r.to ?? "—";
                    }
                    return base;
                }));
            } else {
                setRows([]);
            }
        } catch { setRows([]); }
        finally { setLoading(false); }
    }, [token]);

    useEffect(() => {
        fetchRows(subTab, pairId, startDate, endDate);
    }, [subTab, pairId, startDate, endDate, fetchRows]);

    /* ── column headers per sub-tab ── */
    const headers = subTab === "interest"
        ? ["Time", "Pair", "Coin", "Amount Charged", "Loan Amount", "Hourly Rate", "APR", "Status"]
        : subTab === "transfer"
            ? ["Time", "Pair", "Coin", "Type", "Amount", "From", "To"]
            : ["Time", "Pair", "Coin", "Type", "Amount", "Status"];

    return (
        <div className="cnt_table active">
            <div className="agce-margin-subtabs">
                {AH_SUB_TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setSubTab(t.id)}
                        className={`agce-margin-subtab-btn ${subTab === t.id ? "active" : ""}`}
                    >{t.label}</button>
                ))}
            </div>

            {/* ── Date filter bar ── */}
            <FilterBarScrollable>
                <CustomFilterDropdown
                    options={[
                        { value: "", label: "Time Range" },
                        { value: "1D", label: "1D" },
                        { value: "7D", label: "7D" },
                        { value: "30D", label: "30D" },
                        { value: "90D", label: "90D" }
                    ]}
                    value={preset}
                    onChange={(val) => applyPreset(val)}
                    label="Time Range"
                />
                <MarginDateInput
                    value={startDate}
                    placeholder="From"
                    onChange={(e) => { setStartDate(e.target.value); setPreset(""); }}
                />
                <span className="agce-margin-filter-arrow">→</span>
                <MarginDateInput
                    value={endDate}
                    placeholder="To"
                    onChange={(e) => { setEndDate(e.target.value); setPreset(""); }}
                />
                <button type="button" onClick={() => { setStartDate(""); setEndDate(""); setPreset(""); }}
                    className="agce-margin-filter-reset"
                >Reset <i className="ri-refresh-line" /></button>
            </FilterBarScrollable>

            {/* ── Desktop table ── */}
            <div className="desktop_view2">
                <div className="table-responsive spot_orders_table_wrap" style={{ height: "310px", minHeight: "280px", overflowY: "auto" }}>
                    <table className="table market_trade_table">
                        <thead>
                            <tr>
                                {headers.map((h) => <th key={h}>{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={headers.length} className="text-center py-4">Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={headers.length}>
                                        <div className="spot_orders_empty_state" role="status">
                                            <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                        </div>
                                    </td>
                                </tr>
                            ) : rows.map((r) => (
                                <tr key={r.id}>
                                    <td style={{ whiteSpace: "nowrap", fontSize: 11 }}>{r.time}</td>
                                    <td>{r.pair}</td>
                                    <td>{r.coin}</td>
                                    {subTab === "interest" ? <>
                                        <td>{r.amount}</td>
                                        <td>{r.loanAmount}</td>
                                        <td>{r.hourlyRate}</td>
                                        <td>{r.apr}</td>
                                        <td><span style={{ color: "#01bc8d", fontSize: 11 }}>{r.status}</span></td>
                                    </> : subTab === "transfer" ? <>
                                        <td style={{ color: r.transferType === "Transfer In" ? "#01bc8d" : "#e45561", whiteSpace: "nowrap" }}>
                                            {r.transferType}
                                        </td>
                                        <td>{r.amount}</td>
                                        <td style={{ fontSize: 11, color: "#8E949E", whiteSpace: "nowrap" }}>{r.from}</td>
                                        <td style={{ fontSize: 11, color: "#8E949E", whiteSpace: "nowrap" }}>{r.to}</td>
                                    </> : <>
                                        <td style={{ textTransform: "capitalize" }}>{subTab}</td>
                                        <td>{r.amount}</td>
                                        <td><span style={{ color: "#01bc8d", fontSize: 11 }}>{r.status}</span></td>
                                    </>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Mobile cards ── */}
            <div className="order_history_mobile_view twomobile so-mobile" style={{ minHeight: 200 }}>
                {loading ? (
                    <div className="text-center py-4" style={{ fontSize: 12, color: "#8E949E" }}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="spot_orders_empty_state py-4" role="status">
                        <img src="/images/no-data.svg" alt="" width={100} height={120} className="spot_orders_empty_telescope" />
                    </div>
                ) : rows.map((r) => (
                    <OrderMobileCardShell key={r.id} popupTitle={subTab.charAt(0).toUpperCase() + subTab.slice(1) + " details"}>
                        <div className="so-mobileCard__row"><span>Time</span><span>{r.time}</span></div>
                        <div className="so-mobileCard__row"><span>Pair</span><span>{r.pair}</span></div>
                        <div className="so-mobileCard__row"><span>Coin</span><span>{r.coin}</span></div>
                        {subTab === "transfer" ? <>
                            <div className="so-mobileCard__row">
                                <span>Type</span>
                                <span style={{ color: r.transferType === "Transfer In" ? "#01bc8d" : "#e45561" }}>
                                    {r.transferType}
                                </span>
                            </div>
                            <div className="so-mobileCard__row"><span>Amount</span><span>{r.amount}</span></div>
                            <div className="so-mobileCard__row"><span>From</span><span>{r.from}</span></div>
                            <div className="so-mobileCard__row"><span>To</span><span>{r.to}</span></div>
                        </> : subTab === "interest" ? <>
                            <div className="so-mobileCard__row"><span>Amount Charged</span><span>{r.amount}</span></div>
                            <div className="so-mobileCard__row"><span>Loan Amount</span><span>{r.loanAmount}</span></div>
                            <div className="so-mobileCard__row"><span>Hourly Rate</span><span>{r.hourlyRate}</span></div>
                            <div className="so-mobileCard__row"><span>APR</span><span>{r.apr}</span></div>
                            <div className="so-mobileCard__row"><span>Status</span><span style={{ color: "#01bc8d" }}>{r.status}</span></div>
                        </> : <>
                            <div className="so-mobileCard__row"><span>Type</span><span style={{ textTransform: "capitalize" }}>{subTab}</span></div>
                            <div className="so-mobileCard__row"><span>Amount</span><span>{r.amount}</span></div>
                            <div className="so-mobileCard__row"><span>Status</span><span style={{ color: "#01bc8d" }}>{r.status}</span></div>
                        </>}
                    </OrderMobileCardShell>
                ))}
            </div>
        </div>
    );
}

/** Primary tabs row (Open Orders / Order History / …) — arrows when list overflows on mobile */
function SpotOrdersPrimaryTabsScroll({ positionOrderTab, setPositionOrderTab }) {
    const scrollerRef = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateArrows = useMemo(
        () => () => {
            const el = scrollerRef.current;
            if (!el) return;
            const max = el.scrollWidth - el.clientWidth;
            const left = el.scrollLeft;
            setCanLeft(left > 2);
            setCanRight(max > 2 && left < max - 2);
        },
        []
    );

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;
        updateArrows();
        el.addEventListener("scroll", updateArrows, { passive: true });
        window.addEventListener("resize", updateArrows);
        const t = window.setTimeout(updateArrows, 150);
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateArrows) : null;
        if (ro) ro.observe(el);
        return () => {
            window.clearTimeout(t);
            el.removeEventListener("scroll", updateArrows);
            window.removeEventListener("resize", updateArrows);
            if (ro) ro.disconnect();
        };
    }, [updateArrows, positionOrderTab]);

    const scrollByAmount = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const amount = Math.max(120, Math.floor(el.clientWidth * 0.6));
        el.scrollBy({ left: dir * amount, behavior: "smooth" });
    };

    return (
        <div className="spot_orders_primary_tabs_scrollWrap">
            <button
                type="button"
                className={`spot_orders_chipsArrow spot_orders_chipsArrow--left${canLeft ? "" : " is-hidden"}`}
                aria-label="Scroll tabs left"
                onClick={() => scrollByAmount(-1)}
            >
                <i className="ri-arrow-left-s-line" aria-hidden="true" />
            </button>

            <ul className="position_list spot_orders_primary_tabs spot_orders_primary_tabs--scroller" ref={scrollerRef}>
                <li className={`nav-item ${positionOrderTab === "size" || positionOrderTab === "positionHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("size")}>Positions</button>
                </li>
                <li className={`nav-item ${positionOrderTab === "positions" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("positions")}>Open Orders</button>
                </li>
                <li className={`nav-item ${positionOrderTab === "orderHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("orderHistory")}>Order History</button>
                </li>
                <li className={`nav-item ${positionOrderTab === "tradeHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("tradeHistory")}>Trade History</button>
                </li>
                <li className={`nav-item ${positionOrderTab === "loanManagement" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("loanManagement")}>Loan Management</button>
                </li>
                <li className={`nav-item ${positionOrderTab === "assetHistory" ? "active" : ""}`} role="presentation">
                    <button type="button" onClick={() => setPositionOrderTab("assetHistory")}>Asset History</button>
                </li>
            </ul>

            <button
                type="button"
                className={`spot_orders_chipsArrow spot_orders_chipsArrow--right${canRight ? "" : " is-hidden"}`}
                aria-label="Scroll tabs right"
                onClick={() => scrollByAmount(1)}
            >
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </button>
        </div>
    );
}

function orderHistoryLimitOrMarketPrice(item, safeToFixed8) {
    const raw = item?.price;
    if (raw != null && String(raw).trim() !== "") {
        const n = Number(raw);
        if (Number.isFinite(n)) return safeToFixed8(raw, "0");
    }
    const t = String(item?.type || item?.order_type || "").toUpperCase();
    if (t === "MARKET") return "Market";
    return "—";
}

function orderHistoryAvgFillPrice(item, safeToFixed8, filledQtyFn) {
    const filled = filledQtyFn(item);
    const raw = item?.avg_execution_price;
    if (filled <= 0 && (raw == null || String(raw).trim() === "" || Number(raw) === 0)) return "—";
    return safeToFixed8(raw, "—");
}

/** Spot order/tables BUY·SELL — dedicated classes (+ !important) beat .spot_orders_table tbody td. */
function spotSideColorClass(side) {
    const s = String(side ?? "").trim().toUpperCase();
    if (s === "BUY") return "agce-spot-side-buy";
    if (s === "SELL") return "agce-spot-side-sell";
    return "";
}

/** Hero subtitle (side + type/role) with buy/sell tint — matches profile `OrderMobileCardShell` rows. */
function mobileHeroSideTypeSubtitle(side, secondLabel) {
    const typeStr = secondLabel != null && String(secondLabel).trim() !== "" ? String(secondLabel).trim() : "";
    const parts = [side, typeStr].filter((x) => x && x !== "—");
    const line = parts.join(" · ");
    if (!line) return null;
    const s = String(side ?? "").toUpperCase();
    const tone = s === "BUY" ? "orders-mobCard__subtype--buy" : s === "SELL" ? "orders-mobCard__subtype--sell" : "";
    return <span className={`orders-mobCard__subtype ${tone}`.trim()}>{line}</span>;
}

const TRADE_OPEN_ORDERS_DESKTOP_COLS = 13;
const TRADE_TRADE_HISTORY_DESKTOP_COLS = 9;
const TRADE_ORDER_HISTORY_DESKTOP_COLS = 15;

/**
 * Combine multiple executions at the same trading price into one row
 * (aggregate quantity & fee); preserves first-seen price ordering.
 */
function aggregateOrderHistoryExecutedLegs(item) {
    const executedPrices = Array.isArray(item?.executed_prices) ? item.executed_prices : [];
    if (executedPrices.length === 0) return [];

    const priceGroupKey = (p) => {
        const n = Number(p);
        if (!Number.isFinite(n)) return `s:${String(p ?? "")}`;
        return `n:${n.toFixed(14)}`;
    };

    const keysInOrder = [];
    const buckets = new Map();

    executedPrices.forEach((trade) => {
        const gk = priceGroupKey(trade?.price);
        if (!buckets.has(gk)) {
            keysInOrder.push(gk);
            buckets.set(gk, {
                price: trade?.price,
                sumQty: 0,
                sumFee: 0,
            });
        }
        const b = buckets.get(gk);
        b.sumQty += Number(trade?.quantity) || 0;
        b.sumFee += Number(trade?.fee) || 0;
    });

    return keysInOrder.map((gk, idx) => {
        const b = buckets.get(gk);
        return {
            reactKey: `agg:${gk}:${idx}`,
            price: b.price,
            quantity: b.sumQty,
            fee: b.sumFee,
        };
    });
}

function loanFmt(v, dp = 8) {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return "0";
    return n.toFixed(dp).replace(/\.?0+$/, "") || "0";
}

/** Cross margin borrow/repay modal — uses GET /debts, /borrowable and POST /borrow, /repay. */
function LoanBorrowRepayModal({ loan, baseCoin, quoteCoin, buyCoinBal, sellCoinBal, initialTab, onClose, onSuccess }) {
    const [tab, setTab] = useState(initialTab || "repay");
    const [coin, setCoin] = useState(loan?.coin || baseCoin || "");
    const [amount, setAmount] = useState("");
    const [repayFull, setRepayFull] = useState(false);
    const [busy, setBusy] = useState(false);
    const [crossDebts, setCrossDebts] = useState([]);
    const [crossData, setCrossData] = useState(null);
    const [crossBorrowable, setCrossBorrowable] = useState(null);

    const isBorrow = tab === "borrow";
    const isObjectId = (v) => typeof v === "string" && /^[a-f\d]{24}$/i.test(v);

    const refreshCrossDebts = useCallback(async () => {
        try {
            const res = await AuthService.crossDebts();
            if (res?.success) setCrossDebts(res.data?.debts || []);
            else setCrossDebts([]);
        } catch {
            setCrossDebts([]);
        }
    }, []);

    const refreshCrossAccount = useCallback(async () => {
        try {
            const res = await AuthService.crossAccount();
            if (res?.success) setCrossData(res.data);
        } catch {
            /* silent */
        }
    }, []);

    const refreshCrossBorrowable = useCallback(async (currencyId) => {
        if (!currencyId) {
            setCrossBorrowable(null);
            return;
        }
        try {
            const res = await AuthService.crossBorrowable(currencyId);
            if (res?.success) setCrossBorrowable(res.data);
            else setCrossBorrowable(null);
        } catch {
            setCrossBorrowable(null);
        }
    }, []);

    useEffect(() => {
        void refreshCrossDebts();
        void refreshCrossAccount();
    }, [refreshCrossDebts, refreshCrossAccount]);

    useEffect(() => {
        if (isBorrow) return;
        void refreshCrossDebts();
        void refreshCrossAccount();
        const poll = setInterval(() => {
            void refreshCrossDebts();
            void refreshCrossAccount();
        }, 5000);
        return () => clearInterval(poll);
    }, [isBorrow, coin, refreshCrossDebts, refreshCrossAccount]);

    const crossDebtObj = crossDebts.find((d) => d.asset === coin) || null;
    const crossAssetObj = crossData?.assets?.find((a) => a.asset === coin);
    const crossCurrencyId =
        (isObjectId(loan?.currency_id) ? loan.currency_id : null) ||
        (isObjectId(crossDebtObj?.currency_id) ? crossDebtObj.currency_id : null) ||
        (isObjectId(crossAssetObj?.currency_id) ? crossAssetObj.currency_id : null) ||
        null;

    useEffect(() => {
        if (!isBorrow || !crossCurrencyId) {
            setCrossBorrowable(null);
            return;
        }
        void refreshCrossBorrowable(crossCurrencyId);
    }, [isBorrow, crossCurrencyId, coin, refreshCrossBorrowable]);

    useEffect(() => {
        if (loan?.coin) setCoin(loan.coin);
    }, [loan?.coin]);

    useEffect(() => {
        setTab(initialTab || "repay");
        setAmount("");
        setRepayFull(false);
    }, [initialTab, loan?.coin]);

    const crossOutstandingTotal = crossDebtObj
        ? parseFloat(
            crossDebtObj.outstanding
            ?? (parseFloat(crossDebtObj.principal || 0) + parseFloat(crossDebtObj.interest_accrued || 0))
        )
        : (loan?.coin === coin ? parseFloat(loan?.outstanding || 0) : 0);

    const socketBal = coin === baseCoin ? sellCoinBal : buyCoinBal;
    const crossFreeBalance = parseFloat(crossAssetObj?.balance ?? socketBal ?? 0);
    const crossMaxBorrow = crossBorrowable?.borrowable
        ? parseFloat(crossBorrowable.borrowable).toFixed(8).replace(/\.?0+$/, "")
        : "0";
    const crossMaxRepay = Math.min(crossFreeBalance, crossOutstandingTotal)
        .toFixed(8)
        .replace(/\.?0+$/, "");

    const hourlyRate = loan?.hourly_rate_pct != null ? `${loan.hourly_rate_pct}%` : "—";
    const annualRate = loan?.apr_pct != null ? `${loan.apr_pct}%` : "—";

    const switchTab = (t) => { setTab(t); setAmount(""); setRepayFull(false); };

    const handleConfirm = async () => {
        if (!crossCurrencyId) {
            alertErrorMessage("Currency ID not available. Please try again.");
            return;
        }
        setBusy(true);
        try {
            let result;
            if (isBorrow) {
                if (!amount || parseFloat(amount) <= 0) {
                    alertErrorMessage("Enter a valid amount");
                    setBusy(false);
                    return;
                }
                if (parseFloat(amount) > parseFloat(crossMaxBorrow)) {
                    alertErrorMessage("Amount exceeds borrowable limit");
                    setBusy(false);
                    return;
                }
                result = await AuthService.crossBorrow({ currency_id: crossCurrencyId, amount });
            } else {
                if (crossOutstandingTotal <= 0) {
                    alertErrorMessage("No outstanding loan for this asset");
                    setBusy(false);
                    return;
                }
                if (!repayFull && (!amount || parseFloat(amount) <= 0)) {
                    alertErrorMessage("Enter a valid amount");
                    setBusy(false);
                    return;
                }
                if (!repayFull && parseFloat(amount) > parseFloat(crossMaxRepay)) {
                    alertErrorMessage("Amount exceeds repayable limit");
                    setBusy(false);
                    return;
                }
                result = await AuthService.crossRepay({
                    currency_id: crossCurrencyId,
                    amount: repayFull ? undefined : amount,
                });
            }
            if (result?.success) {
                if (!isBorrow && result.data?.fully_repaid) {
                    setCrossDebts((prev) => prev.filter((d) => d.asset !== coin));
                }
                await Promise.all([refreshCrossAccount(), refreshCrossDebts()]);
                if (isBorrow) await refreshCrossBorrowable(crossCurrencyId);
                alertSuccessMessage(result.message || `${isBorrow ? "Borrowed" : "Repaid"} successfully`);
                setAmount("");
                setRepayFull(false);
                if (onSuccess) await onSuccess();
                if (!isBorrow && result.data?.fully_repaid) onClose();
            } else {
                alertErrorMessage(result?.message || "Operation failed");
            }
        } catch (err) {
            alertErrorMessage(err?.message || "Operation failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="modal-backdrop fade show" onClick={() => { if (!busy) onClose(); }} />
            <div className="modal fade kyc_modal show d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div className="margin-modal-tabs">
                                <span
                                    className={`margin-modal-tab${isBorrow ? " margin-modal-tab--active" : ""}`}
                                    onClick={() => switchTab("borrow")}
                                >
                                    Borrow
                                </span>
                                <span
                                    className={`margin-modal-tab${!isBorrow ? " margin-modal-tab--active" : ""}`}
                                    onClick={() => switchTab("repay")}
                                >
                                    Repay
                                </span>
                            </div>
                            <button type="button" className="btn-close" aria-label="Close" onClick={() => { if (!busy) onClose(); }} />
                        </div>

                        <div className="modal-body">
                            <div className="wallet_transfer">
                                {/* Pairs — single pair, no dropdown */}
                                <div className="crypto_selectcoin crypto_selectcoin-2">
                                    <div className="margin-pairs-header">
                                        <h6>Cross Margin</h6>
                                    </div>
                                    <div className="coin_cryptofiled">
                                        <button type="button" style={{ cursor: "default", pointerEvents: "none" }}>
                                            <span>{baseCoin}/{quoteCoin}</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Coin radios */}
                                <div className="margin-coin-radios">
                                    {[baseCoin, quoteCoin].filter(Boolean).map((c) => (
                                        <label key={c} className="margin-coin-radio">
                                            <input
                                                type="radio"
                                                name="loan-modal-coin"
                                                checked={coin === c}
                                                onChange={() => { setCoin(c); setAmount(""); }}
                                            />
                                            <span>{c}</span>
                                        </label>
                                    ))}
                                </div>

                                {isBorrow ? (
                                    <>
                                        {crossBorrowable && (
                                            <div className="margin-info-rows mb-2">
                                                <div className="margin-info-row">
                                                    <span>Borrowable</span>
                                                    <span>{crossMaxBorrow} {coin}</span>
                                                </div>
                                                {crossBorrowable.binding && (
                                                    <div className="margin-info-row">
                                                        <span>Limited by</span>
                                                        <span style={{ color: "#8E949E", fontSize: 12 }}>
                                                            {crossBorrowable.binding === "equity" ? "Your margin capacity"
                                                                : crossBorrowable.binding === "pool" ? "Lending pool liquidity"
                                                                : crossBorrowable.binding === "user_cap" ? "Per-user limit"
                                                                : crossBorrowable.binding}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="crypto_selectcoin crypto_selectcoin-2">
                                            <h6>Loan Amount</h6>
                                            <div className="price_max_total">
                                                <input
                                                    type="number"
                                                    placeholder="Enter amount to borrow"
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <div className="d-flex gap-0">
                                                    {coin}
                                                    <button type="button" onClick={() => setAmount(crossMaxBorrow)}>Max</button>
                                                </div>
                                            </div>
                                        </div>
                                        {(loan?.hourly_rate_pct != null || loan?.apr_pct != null) && (
                                            <div className="margin-info-rows">
                                                <div className="margin-info-row">
                                                    <span>Hourly Interest Rate</span><span>{hourlyRate}</span>
                                                </div>
                                                <div className="margin-info-row">
                                                    <span>Annualized Interest Rate</span><span>{annualRate}</span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : crossOutstandingTotal > 0 ? (
                                    <>
                                        <div className="margin-info-rows mt-2 mb-2">
                                            <div className="margin-info-row">
                                                <span>Principal</span>
                                                <span>{loanFmt(crossDebtObj?.principal)} {coin}</span>
                                            </div>
                                            <div className="margin-info-row">
                                                <span>Accrued Interest</span>
                                                <span style={{ color: parseFloat(crossDebtObj?.interest_accrued) > 0 ? "#e45561" : undefined }}>
                                                    {loanFmt(crossDebtObj?.interest_accrued)} {coin}
                                                </span>
                                            </div>
                                            <div className="margin-info-row" style={{ borderTop: "1px solid rgba(128,128,128,0.15)", paddingTop: 4, marginTop: 2 }}>
                                                <span>Total Owed</span>
                                                <span style={{ fontWeight: 600 }}>{loanFmt(crossOutstandingTotal)} {coin}</span>
                                            </div>
                                            <div className="margin-info-row">
                                                <span>Free Balance</span>
                                                <span>{loanFmt(crossFreeBalance)} {coin}</span>
                                            </div>
                                        </div>
                                        <label className="d-flex align-items-center gap-2 mb-3" style={{ cursor: "pointer", fontSize: 13 }}>
                                            <input
                                                type="checkbox"
                                                checked={repayFull}
                                                onChange={(e) => { setRepayFull(e.target.checked); setAmount(""); }}
                                                style={{ width: 14, height: 14 }}
                                            />
                                            Repay All (clears full debt including accrued interest)
                                        </label>
                                        {!repayFull && (
                                            <div className="crypto_selectcoin crypto_selectcoin-2">
                                                <h6>Repayment Amount</h6>
                                                <div className="price_max_total">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter amount to repay"
                                                        value={amount}
                                                        onChange={(e) => setAmount(e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    <div className="d-flex gap-0">
                                                        {coin}
                                                        <button type="button" onClick={() => setAmount(crossMaxRepay)}>Max</button>
                                                    </div>
                                                </div>
                                                <legend className="margin-modal-legend" style={{ marginTop: 4, fontSize: "12px" }}>
                                                    Max repayable: <span>{loanFmt(crossMaxRepay)} {coin}</span>
                                                </legend>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="margin-modal-empty-note mt-3">
                                        <i className="ri-error-warning-line empty-note-icon" />
                                        <span>You haven&apos;t borrowed any {coin} yet.</span>
                                    </div>
                                )}

                                {/* Note */}
                                {(isBorrow || crossOutstandingTotal > 0) && (
                                    <div className="margin-modal-note">
                                        <i className="ri-information-fill" />
                                        {isBorrow ? (
                                            <span>Borrowed funds are available immediately. Interest accrues against your shared collateral.</span>
                                        ) : (
                                            <span>Interest is settled first, then principal. Partial repayment is allowed at any time.</span>
                                        )}
                                    </div>
                                )}

                                {/* Actions */}
                                {(isBorrow || crossOutstandingTotal > 0) && (
                                    <div className="margin-modal-actions">
                                        <button type="button" className="btn margin-modal-btn-cancel" disabled={busy} onClick={() => { if (!busy) onClose(); }}>Cancel</button>
                                        <button type="button" className="btn margin-modal-btn-confirm" disabled={busy} onClick={handleConfirm}>
                                            {busy ? "Processing…" : isBorrow ? "Confirm Borrow" : "Confirm Repay"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

const TradeHistorySection = (props) => {
    const {
        BuyCoinBal,
        SPOT_OPEN_ORDER_KINDS,
        SelectedCoin,
        SellCoinBal,
        cancelOrder,
        expandedRowIndex,
        fetchSpotWallets,
        formatOrderHistoryDateTime,
        formatSpotOpenOrderDateTime,
        formatTradeHistoryDateTime,
        formatTotal,
        getOpenOrderPair,
        marginPositionData,
        marginPositionHistoryRows,
        matchesOpenOrderKind,
        myTrades,
        nineDecimalFormat,
        onSpotOrderHistoryScrollViewport,
        onSpotTradeHistoryScrollViewport,
        openOrderKindTab,
        openOrders,
        orderHistoryExecutedValueNum,
        orderHistoryMarketLabel,
        orderHistoryQuoteCurrency,
        orderHistoryStatusClass,
        orderHistoryStatusLabel,
        spotOrderLifecycleStatus,
        orderType,
        pastOrderType,
        pastOrders,
        ohSideFilter,
        setOhSideFilter,
        ohPreset,
        setOhPreset,
        ohStartDate,
        setOhStartDate,
        ohEndDate,
        setOhEndDate,
        applyOhPreset,
        positionOrderTab,
        safeToFixed8,
        setExpandedRowIndex,
        setOpenOrderKindTab,
        setPositionOrderTab,
        setShowExecutedTrades,
        setorderType,
        setpastOrderType,
        showExecutedTrades,
        spotHistoryViewportKey,
        spotOpenOrderFilledQty,
        spotOpenOrderRemainingQty,
        spotOpenOrderTifLabel,
        spotOpenOrderTypeLabel,
        token,
        tradeHistoryBaseAsset,
        tradeHistoryMarketLabel,
        tradeHistoryQuoteAsset,
        tradeHistoryRoleLabel,
        tradeHistorySideFilter,
        setTradeHistorySideFilter,
        thPreset,
        setThPreset,
        thStartDate,
        setThStartDate,
        thEndDate,
        setThEndDate,
        applyThPreset,
        posHistSideFilter,
        setPosHistSideFilter,
        posHistStartDate,
        setPosHistStartDate,
        posHistEndDate,
        setPosHistEndDate,
        posHistPreset,
        setPosHistPreset,
        closePositionModal,
        setClosePositionModal,
        closePositionLoading,
        closingPositionPairs = {},
        handleClosePosition,
        loanRows,
        repayModalLoan,
        setRepayModalLoan,
        loanPairId,
        loanBaseCoin,
        loanQuoteCoin,
        loanBuyCoinBal,
        loanSellCoinBal,
        onLoanRefresh,
        marginBalance,
        crossRisk = null,
    } = props;

    const parsedCrossRisk = useMemo(() => parseCrossRisk(crossRisk || {}), [crossRisk]);
    const [crossRiskModalOpen, setCrossRiskModalOpen] = useState(false);

    // ── Order History local-only UI state (side/date/preset come from props → backend) ──
    const [ohHideCanceled, setOhHideCanceled] = useState(false);
    const [ohCurrentMarket, setOhCurrentMarket] = useState(false);
    const [ohTypeTab, setOhTypeTab] = useState("ALL");

    // ── Trade History local-only UI state ──
    const [thCurrentMarket, setThCurrentMarket] = useState(false);

    // ── 2-second cooldown after cancel / close click ──
    const [cooldownIds, setCooldownIds] = useState(new Set());
    const startCooldown = useCallback((id) => {
        setCooldownIds((prev) => new Set([...prev, id]));
        setTimeout(() => {
            setCooldownIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
        }, 5000);
    }, []);

    useEffect(() => {
        const el = document.querySelector('.trade_account_summary_assets');
        if (!el) return;

        const handleScroll = () => {
            const rect = el.getBoundingClientRect();
            // 80px is the top sticky offset. Add a 1px buffer to ensure it triggers correctly.
            if (rect.top <= 81) {
                el.classList.add('is-stuck');
            } else {
                el.classList.remove('is-stuck');
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <div className="trade_account_summary_assets">

                <div className="trade_summary_table_lft mt-0 position_order trade_summary_table_lft_position_order">
                    <div className="top_th_easyop border-0 spot_orders_top">
                        <SpotOrdersPrimaryTabsScroll positionOrderTab={positionOrderTab} setPositionOrderTab={setPositionOrderTab} />
                        {/* {positionOrderTab === "orderHistory" || positionOrderTab === "tradeHistory" ? (
                                        <div className="spot_orders_filter_actions px-2 py-1 justify-content-end">
                                            <Link
                                                className="spot_orders_reset"
                                                to={
                                                    positionOrderTab === "orderHistory"
                                                        ? "/user_profile/orders/spot_order_history"
                                                        : "/user_profile/orders/spot_position_history"
                                                }
                                            >
                                                View all
                                            </Link>
                                        </div>
                                    ) : null} */}
                    </div>

                    {positionOrderTab === "positions" ? (
                        <SpotOpenOrdersFilterRow
                            SPOT_OPEN_ORDER_KINDS={SPOT_OPEN_ORDER_KINDS}
                            openOrderKindTab={openOrderKindTab}
                            setOpenOrderKindTab={setOpenOrderKindTab}
                            orderType={orderType}
                            setorderType={setorderType}
                        />
                    ) : null}

                    {positionOrderTab === "size" && (
                        <div className="cnt_table active">
                            {(() => {
                                const positions = Array.isArray(marginPositionData) ? marginPositionData : (marginPositionData ? [marginPositionData] : []);
                                const fmt4   = (v) => { const n = parseFloat(v || 0); return Number.isFinite(n) ? n.toFixed(4) : "—"; };
                                const fmtQty = (v) => { const n = parseFloat(v || 0); return n.toFixed(8).replace(/\.?0+$/, "") || "0"; };

                                const posRow = (pos) => {
                                    const ml = pos?.margin_level != null ? parseFloat(pos.margin_level) : null;
                                    const mlRisk = ml != null && ml < 1.1;
                                    const pnl      = parseFloat(pos?.unrealized_pnl || 0);
                                    // roe_pct from cross API; fall back to unrealized_pnl_pct
                                    const pnlPct   = parseFloat((pos?.roe_pct ?? pos?.unrealized_pnl_pct) || 0);
                                    const realPnl  = parseFloat(pos?.realized_pnl || 0);
                                    // Cross positions use asset (BTC) not pair (BTCUSDT)
                                    const quoteAsset = pos?.quote_asset || "USDT";
                                    const pairLabel  = pos?.pair
                                        ? `${pos.pair.slice(0, -(quoteAsset.length))}/${quoteAsset}`
                                        : (pos?.asset ? `${pos.asset}/${quoteAsset}` : "—");
                                    const liqPrice    = pos?.liquidation_price
                                        ? parseFloat(pos.liquidation_price).toFixed(2)
                                        : (pos?.warning_price ? parseFloat(pos.warning_price).toFixed(2) : "—");
                                    const isLong      = pos?.side === "LONG";
                                    const mmRatioDisplay = (() => {
                                        if (ml == null) return "—";
                                        if (ml >= 999) return "999+%";
                                        return `${ml.toFixed(0)}%`;
                                    })();
                                    // net_quantity → quantity (normalized at fetch); value_usdt → notional
                                    const holding    = fmtQty(pos?.quantity ?? pos?.net_quantity);
                                    const notional   = fmt4(pos?.notional ?? pos?.value_usdt);
                                    const entryPrice = (pos?.entry_price == null || parseFloat(pos.entry_price) === 0) ? "—" : fmt4(pos.entry_price);
                                    const indexPrice = fmt4(pos?.index_price ?? pos?.mark_price);
                                    return { ml, mlRisk, pnl, pnlPct, realPnl, pairLabel, quoteAsset, liqPrice, isLong, mmRatioDisplay, holding, notional, entryPrice, indexPrice };
                                };

                                return (
                                    <>
                                        {/* ── Desktop table ── */}
                                        <div className="desktop_view2">
                                            <div className="table-responsive spot_orders_table_wrap" style={{ minHeight: "200px" }}>
                                                <table className="table table_home spot_orders_table">
                                                    <thead>
                                                        <tr>
                                                            <th>Pair / Leverage</th>
                                                            <th>Holding</th>
                                                            <th>Value</th>
                                                            <th>Entry Price</th>
                                                            <th>Index Price</th>
                                                            <th>Unrealized PnL / Return</th>
                                                            <th>Realized PnL</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {positions.length === 0 ? (
                                                            <tr className="no-data-row spot_orders_no_data_row">
                                                                <td colSpan={10}>
                                                                    <div className="spot_orders_empty_state" role="status">
                                                                        <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ) : positions.map((pos, idx) => {
                                                            const { ml, mlRisk, pnl, pnlPct, realPnl, pairLabel, quoteAsset, liqPrice, isLong, mmRatioDisplay, holding, notional, entryPrice, indexPrice } = posRow(pos);
                                                            return (
                                                                <tr key={pos?.pair || idx}>
                                                                    {/* {mlRisk && (
                                                                        <td colSpan={10} style={{ padding: "2px 8px" }}>
                                                                            <div className="alert alert-danger py-1 px-2 mb-1 small">
                                                                                <i className="ri-alarm-warning-line me-1" />
                                                                                LIQUIDATION RISK — {pairLabel} margin level {ml?.toFixed(2)} is critically low.
                                                                            </div>
                                                                        </td>
                                                                    )} */}
                                                                    <td>
                                                                        <div style={{ fontWeight: 700, fontSize: 13 }}>{pairLabel}</div>
                                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                                                                            <span style={{ fontWeight: 700, fontSize: 12, color: isLong ? "#16a34a" : "#dc2626", border: `1px solid ${isLong ? "#16a34a" : "#dc2626"}`, borderRadius: 3, padding: "1px 5px" }}>
                                                                                {isLong ? "L" : "S"}
                                                                            </span>
                                                                            <span style={{ fontSize: 12, color: "#6b7280" }}>Cross</span>
                                                                            <span style={{ fontSize: 12, color: "#6b7280" }}>{pos?.leverage ? `${pos.leverage}x` : ""}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{holding}</td>
                                                                    <td>{notional} {quoteAsset}</td>
                                                                    <td>{entryPrice}</td>
                                                                    <td>{indexPrice}</td>
                                                                    <td>
                                                                        <div style={{ color: pnl >= 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                                                                            {pnl >= 0 ? "+" : ""}{pnl.toFixed(4)}
                                                                        </div>
                                                                        <small style={{ color: pnl >= 0 ? "#16a34a" : "#dc2626" }}>
                                                                            ({pnl >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%)
                                                                        </small>
                                                                    </td>
                                                                    <td>
                                                                        <div>{realPnl.toFixed(4)}</div>
                                                                        <small style={{ color: "#9ca3af" }}>≈${Math.abs(realPnl).toFixed(2)}</small>
                                                                    </td>
                                                                    <td>
                                                                        {(() => {
                                                                            if (!pos?.position_id) return (
                                                                                <span style={{ position: "relative", display: "inline-block" }} className="agce-no-liab-wrap">
                                                                                    <span style={{ cursor: "help", color: "#9ca3af", fontSize: 13 }}>—</span>
                                                                                    <span className="agce-no-liab-tip">There is no liability on this position</span>
                                                                                </span>
                                                                            );
                                                                            const posVal = Math.abs(parseFloat(pos?.value_usdt ?? pos?.notional ?? 0));
                                                                            const minNotional = parseFloat(SelectedCoin?.min_notional ?? 10);
                                                                            const tooSmall = posVal > 0 && posVal < minNotional;
                                                                            const inCooldown = !!closingPositionPairs[pos?.pair || `${pos?.asset}USDT`] || cooldownIds.has(pos?.pair || pos?.asset);
                                                                            const closeKey = pos?.pair || `${pos?.asset}USDT`;
                                                                            const btn = inCooldown
                                                                                ? <span style={{ fontSize: 12, color: "#8e949e" }}>Closing…</span>
                                                                                : (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn active-position-close-btn"
                                                                                    disabled={!!closingPositionPairs[closeKey]}
                                                                                    onClick={() => {
                                                                                        if (tooSmall || closingPositionPairs[closeKey]) return;
                                                                                        setClosePositionModal(closeKey);
                                                                                    }}
                                                                                >
                                                                                    Market Close
                                                                                </button>
                                                                            );
                                                                            return tooSmall ? (
                                                                                <span className="agce-no-liab-wrap">
                                                                                    {btn}
                                                                                    <span className="agce-no-liab-tip">Only supports positions with a value of more than or equal to {minNotional} USDT. Try transfer to spot.</span>
                                                                                </span>
                                                                            ) : btn;
                                                                        })()}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* ── Mobile cards ── */}
                                        <div className="order_history_mobile_view twomobile so-mobile">
                                            {positions.length === 0 ? (
                                                <div className="no-data-wrapper w-100">
                                                    <div className="spot_orders_empty_state" role="status">
                                                        <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                    </div>
                                                </div>
                                            ) : positions.map((pos, idx) => {
                                                const { ml, mlRisk, pnl, pnlPct, realPnl, pairLabel, quoteAsset, liqPrice, isLong, mmRatioDisplay, holding, notional, entryPrice, indexPrice } = posRow(pos);
                                                return (
                                                    <div key={pos?.pair || idx} className="active-position-mobile-card">
                                                        {mlRisk && (
                                                            <div className="alert alert-danger py-1 px-2 mb-2 small">
                                                                <i className="ri-alarm-warning-line me-1" />
                                                                LIQUIDATION RISK — Margin level {ml?.toFixed(2)}
                                                            </div>
                                                        )}
                                                        <div className="active-position-card-header">
                                                            <div>
                                                                <div className="active-position-card-title">{pairLabel}</div>
                                                                <div className="active-position-card-subtitle-wrapper">
                                                                    <span className={`active-position-side-tag ${isLong ? "active-position-side-tag-long" : "active-position-side-tag-short"}`}>
                                                                        {isLong ? "L" : "S"}
                                                                    </span>
                                                                    <span className="active-position-card-subtitle-item">Cross</span>
                                                                    <span className="active-position-card-subtitle-item">{pos?.leverage ? `${pos.leverage}x` : ""}</span>
                                                                </div>
                                                            </div>
                                                            {(() => {
                                                                if (!pos?.position_id) return (
                                                                    <span
                                                                        title="There is no liability on this position"
                                                                        className="active-position-no-liability"
                                                                    >—</span>
                                                                );
                                                                const posVal = Math.abs(parseFloat(pos?.value_usdt ?? pos?.notional ?? 0));
                                                                const minNotional = parseFloat(SelectedCoin?.min_notional ?? 10);
                                                                const tooSmall = posVal > 0 && posVal < minNotional;
                                                                const mobKey = pos?.pair || `${pos?.asset}USDT`;
                                                                const mobInCooldown = !!closingPositionPairs[mobKey] || cooldownIds.has(mobKey);
                                                                if (mobInCooldown) return <span className="active-position-closing-text">Closing…</span>;
                                                                const mobileBtn = (
                                                                    <button
                                                                        type="button"
                                                                        className="btn active-position-close-btn"
                                                                        disabled={!!closingPositionPairs[mobKey]}
                                                                        style={{ opacity: tooSmall ? 0.5 : 1, cursor: tooSmall ? "not-allowed" : "pointer", pointerEvents: tooSmall ? "none" : "auto" }}
                                                                        onClick={() => {
                                                                            if (tooSmall || closingPositionPairs[mobKey]) return;
                                                                            setClosePositionModal(mobKey);
                                                                        }}
                                                                    >
                                                                        Market Close
                                                                    </button>
                                                                );
                                                                return tooSmall ? (
                                                                    <span className="agce-no-liab-wrap">
                                                                        {mobileBtn}
                                                                        <span className="agce-no-liab-tip">Only supports positions with a value of more than or equal to {minNotional} USDT.</span>
                                                                    </span>
                                                                ) : mobileBtn;
                                                            })()}
                                                        </div>
                                                        {[
                                                            ["Holding", holding],
                                                            ["Value", `${notional} ${quoteAsset}`],
                                                            ["Entry Price", entryPrice],
                                                            ["Index Price", indexPrice],
                                                            ["Unrealized PnL", <span key="upnl" className={pnl >= 0 ? "active-position-pnl-up" : "active-position-pnl-down"}>{pnl >= 0 ? "+" : ""}{pnl.toFixed(4)} ({pnl >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%)</span>],
                                                            ["Realized PnL", `${realPnl.toFixed(4)}`],
                                                        ].map(([label, value]) => (
                                                            <div key={label} className="active-position-detail-row">
                                                                <span className="active-position-detail-label">{label}</span>
                                                                <span className="active-position-detail-value">{value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {positionOrderTab === "positionHistory" && (
                        <div className="cnt_table active">

                            {/* Filter bar — matches Order History / Trade History design */}
                            <FilterBarScrollable>
                                <CustomFilterDropdown
                                    options={[
                                        { value: "All", label: "All Sides" },
                                        { value: "LONG", label: "Long" },
                                        { value: "SHORT", label: "Short" }
                                    ]}
                                    value={posHistSideFilter}
                                    onChange={(val) => setPosHistSideFilter(val)}
                                    label="Side"
                                />

                                <CustomFilterDropdown
                                    options={[
                                        { value: "", label: "Time Range" },
                                        { value: "1D", label: "1D" },
                                        { value: "7D", label: "7D" },
                                        { value: "30D", label: "30D" },
                                        { value: "90D", label: "90D" }
                                    ]}
                                    value={posHistPreset}
                                    onChange={(val) => {
                                        if (!val) {
                                            setPosHistPreset("");
                                            setPosHistStartDate("");
                                            setPosHistEndDate("");
                                            return;
                                        }
                                        const daysMap = { "1D": 1, "7D": 7, "30D": 30, "90D": 90 };
                                        const days = daysMap[val] || 1;
                                        const end = new Date();
                                        const start = new Date();
                                        start.setDate(start.getDate() - days);
                                        const toLocalIso = (d) => {
                                            const y = d.getFullYear();
                                            const mo = String(d.getMonth() + 1).padStart(2, "0");
                                            const dy = String(d.getDate()).padStart(2, "0");
                                            return `${y}-${mo}-${dy}`;
                                        };
                                        setPosHistPreset(val);
                                        setPosHistStartDate(toLocalIso(start));
                                        setPosHistEndDate(toLocalIso(end));
                                    }}
                                    label="Time Range"
                                />

                                <MarginDateInput
                                    value={posHistStartDate}
                                    placeholder="From"
                                    onChange={(e) => { setPosHistStartDate(e.target.value); setPosHistPreset(""); }}
                                />
                                <span className="agce-margin-filter-arrow">→</span>
                                <MarginDateInput
                                    value={posHistEndDate}
                                    placeholder="To"
                                    onChange={(e) => { setPosHistEndDate(e.target.value); setPosHistPreset(""); }}
                                />

                                <button
                                    type="button"
                                    onClick={() => {
                                        setPosHistSideFilter("All");
                                        setPosHistStartDate("");
                                        setPosHistEndDate("");
                                        setPosHistPreset("");
                                    }}
                                    className="agce-margin-filter-reset"
                                >Reset <i className="ri-refresh-line" /></button>
                            </FilterBarScrollable>

                            {(() => {
                                const rows = (marginPositionHistoryRows || []).filter(
                                    (pos) => posHistSideFilter === "All" || pos.side === posHistSideFilter
                                );
                                const fmt4 = (v) => {
                                    const n = parseFloat(v || 0);
                                    return Number.isFinite(n) ? n.toFixed(4) : "—";
                                };
                                const fmtDt = (iso) => {
                                    if (!iso) return null;
                                    const d = new Date(iso);
                                    const pad = (n) => String(n).padStart(2, "0");
                                    return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` };
                                };
                                const statusMap = { CLOSED: "Close All", LIQUIDATED: "Liquidated", OPEN: "Open Position" };

                                const emptyState = (colSpan) => (
                                    <tr className="no-data-row spot_orders_no_data_row">
                                        <td colSpan={colSpan}>
                                            <div className="spot_orders_empty_state" role="status">
                                                <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                            </div>
                                        </td>
                                    </tr>
                                );

                                return (
                                    <>
                                        {/* Desktop table */}
                                        <div className="desktop_view2">
                                            <div className="table-responsive spot_orders_table_wrap" style={{ minHeight: "280px" }}>
                                                <table className="table table_home spot_orders_table">
                                                    <thead>
                                                        <tr>
                                                            <th>Spot/Leverage</th>
                                                            <th>Side</th>
                                                            <th>Status</th>
                                                            <th>Entry Time</th>
                                                            <th>Entry Price</th>
                                                            <th>Exit Price</th>
                                                            <th>Peak Position</th>
                                                            <th>Notional</th>
                                                            <th>Realized PnL</th>
                                                            <th>Fees</th>
                                                            <th>Close-All Time</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!rows.length ? emptyState(11) : rows.map((pos, idx) => {
                                                            const rpnl = parseFloat(pos.realized_pnl || 0);
                                                            const fees = parseFloat(pos.total_fees || 0);
                                                            const qty = parseFloat(pos.quantity || 0);
                                                            const notional = parseFloat(pos.notional || 0);
                                                            const dt = fmtDt(pos.opened_at);
                                                            const dtEnd = fmtDt(pos.closed_at);
                                                            const pairLbl = pos.pair ? `${pos.pair.slice(0, -4)}/${pos.pair.slice(-4)}` : "—";
                                                            const quoteAsset = pos.pair ? pos.pair.slice(-4) : "";
                                                            const baseAsset = pos.pair ? pos.pair.slice(0, -4) : "";
                                                            const isLong = pos.side === "LONG";
                                                            return (
                                                                <tr key={pos._id || idx}>
                                                                    <td>
                                                                        <div className="fw-semibold">{pairLbl}</div>
                                                                        <small className="text-muted">Cross{pos.leverage ? ` · ${pos.leverage}x` : ""}</small>
                                                                    </td>
                                                                    <td>
                                                                        <span style={{ fontWeight: 600, color: isLong ? "#16a34a" : "#dc2626" }}>
                                                                            {isLong ? "L" : "S"}
                                                                        </span>
                                                                    </td>
                                                                    <td>{statusMap[pos.status] || pos.status || "—"}</td>
                                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                                        {dt ? <><div>{dt.date}</div><small style={{ color: "#9ca3af" }}>{dt.time}</small></> : "—"}
                                                                    </td>
                                                                    <td>{(pos.entry_price == null || parseFloat(pos.entry_price) === 0) ? "—" : <>{fmt4(pos.entry_price)}{quoteAsset ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{quoteAsset}</span> : null}</>}</td>
                                                                    <td>{pos.close_price ? <>{fmt4(pos.close_price)}{quoteAsset ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{quoteAsset}</span> : null}</> : "—"}</td>
                                                                    <td>{qty > 0 ? <>{fmt4(qty)}{baseAsset ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{baseAsset}</span> : null}</> : "—"}</td>
                                                                    <td>{notional > 0 ? <>{fmt4(notional)}{quoteAsset ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{quoteAsset}</span> : null}</> : "—"}</td>
                                                                    <td className={rpnl >= 0 ? "pnl-green" : "pnl-danger"}>
                                                                        {rpnl >= 0 ? "+" : ""}{fmt4(rpnl)}
                                                                        {quoteAsset ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{quoteAsset}</span> : null}
                                                                    </td>
                                                                    <td>{fees !== 0 ? <>{fmt4(fees)}{quoteAsset ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{quoteAsset}</span> : null}</> : "—"}</td>
                                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                                        {dtEnd ? <><div>{dtEnd.date}</div><small style={{ color: "#9ca3af" }}>{dtEnd.time}</small></> : "—"}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Mobile cards */}
                                        <div className="order_history_mobile_view twomobile so-mobile">
                                            {!rows.length ? (
                                                <div className="no-data-wrapper w-100">
                                                    <div className="spot_orders_empty_state" role="status">
                                                        <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                    </div>
                                                </div>
                                            ) : rows.map((pos, idx) => {
                                                const rpnl = parseFloat(pos.realized_pnl || 0);
                                                const fees = parseFloat(pos.total_fees || 0);
                                                const qty = parseFloat(pos.quantity || 0);
                                                const notional = parseFloat(pos.notional || 0);
                                                const dt = fmtDt(pos.opened_at);
                                                const dtEnd = fmtDt(pos.closed_at);
                                                const pairLbl = pos.pair ? `${pos.pair.slice(0, -4)}/${pos.pair.slice(-4)}` : "—";
                                                const quoteAsset = pos.pair ? pos.pair.slice(-4) : "";
                                                const baseAsset = pos.pair ? pos.pair.slice(0, -4) : "";
                                                const isLong = pos.side === "LONG";
                                                const sideColor = isLong ? "#16a34a" : "#dc2626";
                                                return (
                                                    <div key={pos._id || idx} className="agce-margin-pos-card">
                                                        {/* Row 1: Spot/Leverage | Side */}
                                                        <div className="agce-margin-pos-row">
                                                            <div className="agce-margin-pos-cell">
                                                                <div className="agce-margin-pos-lbl">Spot/Leverage</div>
                                                                <div className="agce-margin-pos-pair-wrap">
                                                                    <span className="agce-margin-pos-pair"> {pairLbl}</span>
                                                                    <span className="agce-margin-pos-tag">Cross</span>
                                                                    <span className="agce-margin-pos-tag">{statusMap[pos.status] || pos.status || "—"}</span>
                                                                </div>
                                                            </div>
                                                            <div className="agce-margin-pos-cell text-right">
                                                                <div className="agce-margin-pos-lbl">Side</div>
                                                                <div className={`agce-margin-pos-side-val ${isLong ? 'agce-margin-card-success' : 'agce-margin-card-danger'}`}>
                                                                    {isLong ? "L" : "S"}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Row 2: Entry Time | Close-All Time */}
                                                        <div className="agce-margin-pos-row">
                                                            <div className="agce-margin-pos-cell">
                                                                <div className="agce-margin-pos-lbl">Entry Time</div>
                                                                {dt
                                                                    ? <><div className="agce-margin-pos-val-primary">{dt.date}</div><div className="agce-margin-pos-val-secondary">{dt.time}</div></>
                                                                    : <div className="agce-margin-pos-val-primary">—</div>
                                                                }
                                                            </div>
                                                            <div className="agce-margin-pos-cell text-right">
                                                                <div className="agce-margin-pos-lbl">Close-All Time</div>
                                                                {dtEnd
                                                                    ? <><div className="agce-margin-pos-val-primary">{dtEnd.date}</div><div className="agce-margin-pos-val-secondary">{dtEnd.time}</div></>
                                                                    : <div className="agce-margin-pos-val-secondary">—</div>
                                                                }
                                                            </div>
                                                        </div>

                                                        {/* Row 3: Entry Price | Exit Price */}
                                                        <div className="agce-margin-pos-row">
                                                            <div className="agce-margin-pos-cell">
                                                                <div className="agce-margin-pos-lbl">Entry Price</div>
                                                                <div className="agce-margin-pos-val-primary">{(pos.entry_price == null || parseFloat(pos.entry_price) === 0) ? "—" : <>{fmt4(pos.entry_price)} <span className="agce-margin-pos-asset">{quoteAsset}</span></>}</div>
                                                            </div>
                                                            <div className="agce-margin-pos-cell text-right">
                                                                <div className="agce-margin-pos-lbl">Exit Price</div>
                                                                <div className="agce-margin-pos-val-primary">{pos.close_price ? <>{fmt4(pos.close_price)} <span className="agce-margin-pos-asset">{quoteAsset}</span></> : "—"}</div>
                                                            </div>
                                                        </div>

                                                        {/* Row 4: Peak Position | Notional */}
                                                        <div className="agce-margin-pos-row">
                                                            <div className="agce-margin-pos-cell">
                                                                <div className="agce-margin-pos-lbl">Peak Position</div>
                                                                <div className="agce-margin-pos-val-primary">{qty > 0 ? <>{fmt4(qty)} <span className="agce-margin-pos-asset">{baseAsset}</span></> : "—"}</div>
                                                            </div>
                                                            <div className="agce-margin-pos-cell text-right">
                                                                <div className="agce-margin-pos-lbl">Notional</div>
                                                                <div className="agce-margin-pos-val-primary">{notional > 0 ? <>{fmt4(notional)} <span className="agce-margin-pos-asset">{quoteAsset}</span></> : "—"}</div>
                                                            </div>
                                                        </div>

                                                        {/* Row 5: Realized PnL | Fees */}
                                                        <div className="agce-margin-pos-row">
                                                            <div className="agce-margin-pos-cell">
                                                                <div className="agce-margin-pos-lbl">Realized PnL</div>
                                                                <div className={`agce-margin-pos-val-primary ${rpnl >= 0 ? 'agce-margin-card-success' : 'agce-margin-card-danger'}`}>
                                                                    {rpnl >= 0 ? "+" : ""}{fmt4(rpnl)} <span className="agce-margin-pos-asset">{quoteAsset}</span>
                                                                </div>
                                                            </div>
                                                            <div className="agce-margin-pos-cell text-right">
                                                                <div className="agce-margin-pos-lbl">Fees</div>
                                                                <div className="agce-margin-pos-val-primary">{fees !== 0 ? <>{fmt4(fees)} <span className="agce-margin-pos-asset">{quoteAsset}</span></> : "—"}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {positionOrderTab === "positions" && <div className="cnt_table active">

                        <div className="desktop_view2">
                            <div className="table-responsive spot_orders_table_wrap" style={{ minHeight: "320px" }}>

                                <table className="table table_home spot_orders_table">
                                    <thead>
                                        <tr>
                                            <th>Market</th>
                                            <th>Time</th>
                                            <th>Side</th>
                                            <th>Type</th>
                                            <th>TIF</th>
                                            <th>Price</th>
                                            <th>Quantity</th>
                                            <th>Filled</th>
                                            <th>Remaining</th>
                                            <th>Avg Price</th>
                                            <th>Locked</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {openOrders?.length > 0 ? (
                                            openOrders
                                                .filter((item) => (orderType === item?.side || orderType === "All") && matchesOpenOrderKind(item, openOrderKindTab))
                                                .map((item, index) => {
                                                    const d = (v) => v?.$numberDecimal != null ? v.$numberDecimal : v;
                                                    const pair = item?.pair
                                                        ? `${item.pair.slice(0, -4)}/${item.pair.slice(-4)}`
                                                        : getOpenOrderPair(item);
                                                    const filledQty = parseFloat(d(item?.filled_quantity) || 0);
                                                    const remainingQty = parseFloat(d(item?.remaining_quantity) || 0);
                                                    const avgPrice = parseFloat(d(item?.avg_execution_price) || 0);
                                                    const locked = parseFloat(d(item?.locked_amount) || 0);
                                                    return (
                                                        <tr key={item?._id || index}>
                                                            <td>{pair}</td>
                                                            <td>
                                                                <small>
                                                                    <div className="c_view justify-content-start">
                                                                        <span>{formatSpotOpenOrderDateTime(item, "DD/MM/YYYY")}{" "}
                                                                            <small>{formatSpotOpenOrderDateTime(item, "HH:mm:ss")}</small>
                                                                        </span>
                                                                    </div>
                                                                </small>
                                                            </td>
                                                            <td className={spotSideColorClass(item?.side)}>{item?.side}</td>
                                                            <td>{item?.type || "—"}</td>
                                                            <td>{item?.time_in_force || "—"}</td>
                                                            <td>{safeToFixed8(d(item?.price), "—")}</td>
                                                            <td>{nineDecimalFormat(parseFloat(d(item?.quantity) || 0))}</td>
                                                            <td>{nineDecimalFormat(filledQty)}</td>
                                                            <td>{nineDecimalFormat(remainingQty)}</td>
                                                            <td>{avgPrice > 0 ? safeToFixed8(avgPrice, "—") : "—"}</td>
                                                            <td>{locked > 0 ? `${nineDecimalFormat(locked)} ${item?.locked_asset || ""}` : "—"}</td>
                                                            <td>
                                                            {item?.status || "—"}
                                                            </td>
                                                            <td>
                                                                {cooldownIds.has(item?._id)
                                                                    ? <span style={{ fontSize: 12, color: "#8e949e" }}>Cancelling…</span>
                                                                    : <button className="btn text-danger btn-sm btn-icon" type="button" onClick={() => { startCooldown(item?._id); cancelOrder(item?._id); }}><i className="ri-delete-bin-6-line pr-0" /></button>
                                                                }
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                        ) : (
                                            <tr className="no-data-row spot_orders_no_data_row">
                                                <td colSpan={13}>
                                                    <div className="spot_orders_empty_state" role="status">
                                                        <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                            </div>
                        </div>

                        <div className="order_history_mobile_view twomobile so-mobile">
                            <div className="d-flex datalist_mbl flex-column">
                                {openOrders?.length > 0 ? (
                                    openOrders
                                        .filter((item) => (orderType === item?.side || orderType === "All") && matchesOpenOrderKind(item, openOrderKindTab))
                                        .map((item, index) => {
                                            const d = (v) => v?.$numberDecimal != null ? v.$numberDecimal : v;
                                            const pair = item?.pair
                                                ? `${item.pair.slice(0, -4)}/${item.pair.slice(-4)}`
                                                : getOpenOrderPair(item);
                                            const filledQty = parseFloat(d(item?.filled_quantity) || 0);
                                            const remainingQty = parseFloat(d(item?.remaining_quantity) || 0);
                                            const avgPrice = parseFloat(d(item?.avg_execution_price) || 0);
                                            const locked = parseFloat(d(item?.locked_amount) || 0);
                                            return (
                                                <OrderMobileCardShell
                                                    key={item?._id || `oo-${index}`}
                                                    desktopColumnCount={TRADE_OPEN_ORDERS_DESKTOP_COLS}
                                                    popupTitle="Open order details"
                                                    heroPrimary={pair}
                                                    heroSecondary={mobileHeroSideTypeSubtitle(item?.side, item?.type)}
                                                >
                                                    <div className="so-mobileCard__row">
                                                        <span>Date</span>
                                                        <span>{formatSpotOpenOrderDateTime(item, "DD/MM/YYYY")}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Time</span>
                                                        <span>{formatSpotOpenOrderDateTime(item, "HH:mm:ss")}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Side</span>
                                                        <span className={spotSideColorClass(item?.side)}>{item?.side}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Type</span>
                                                        <span>{item?.type || "—"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>TIF</span>
                                                        <span>{item?.time_in_force || "—"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Price</span>
                                                        <span className="so-num">{safeToFixed8(d(item?.price), "—")}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Quantity</span>
                                                        <span className="so-num">{nineDecimalFormat(parseFloat(d(item?.quantity) || 0))}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Filled</span>
                                                        <span className="so-num">{nineDecimalFormat(filledQty)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Remaining</span>
                                                        <span className="so-num">{nineDecimalFormat(remainingQty)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Avg Price</span>
                                                        <span className="so-num">{avgPrice > 0 ? safeToFixed8(avgPrice, "—") : "—"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Locked</span>
                                                        <span className="so-num">{locked > 0 ? `${nineDecimalFormat(locked)} ${item?.locked_asset || ""}` : "—"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Status</span>
                                                        <span className={`badge ${item?.status === "NEW" ? "bg-warning text-dark" : item?.status === "FILLED" ? "bg-success" : "bg-secondary"}`}>
                                                            {item?.status || "—"}
                                                        </span>
                                                    </div>
                                                    <div className="so-mobileCard__actions">
                                                        {cooldownIds.has(item?._id)
                                                            ? <span style={{ fontSize: 12, color: "#8e949e" }}>Cancelling…</span>
                                                            : <button type="button" className="so-cancelBtn" onClick={() => { startCooldown(item?._id); cancelOrder(item?._id); }}>Cancel order</button>
                                                        }
                                                    </div>
                                                </OrderMobileCardShell>
                                            );
                                        })
                                ) : (
                                    <div className="no-data-wrapper w-100">
                                        <div className="spot_orders_empty_state" role="status">
                                            <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>}

                    {positionOrderTab === "tradeHistory" && (
                        <div className="cnt_table active">
                            {/* ── Trade History filter bar — same design as Order History ── */}
                            <FilterBarScrollable>
                                <CustomFilterDropdown
                                    options={[
                                        { value: "All", label: "All Sides" },
                                        { value: "BUY", label: "Buy" },
                                        { value: "SELL", label: "Sell" }
                                    ]}
                                    value={tradeHistorySideFilter}
                                    onChange={(val) => setTradeHistorySideFilter(val)}
                                    label="Side"
                                />

                                <CustomFilterDropdown
                                    options={[
                                        { value: "", label: "Time Range" },
                                        { value: "1D", label: "1D" },
                                        { value: "7D", label: "7D" },
                                        { value: "30D", label: "30D" },
                                        { value: "90D", label: "90D" }
                                    ]}
                                    value={thPreset}
                                    onChange={(val) => applyThPreset(val)}
                                    label="Time Range"
                                />

                                <MarginDateInput
                                    value={thStartDate}
                                    placeholder="From"
                                    onChange={(e) => { setThStartDate(e.target.value); setThPreset(""); }}
                                />
                                <span className="agce-margin-filter-arrow">→</span>
                                <MarginDateInput
                                    value={thEndDate}
                                    placeholder="To"
                                    onChange={(e) => { setThEndDate(e.target.value); setThPreset(""); }}
                                />

                                <button
                                    type="button"
                                    onClick={() => { setTradeHistorySideFilter("All"); setThPreset(""); setThStartDate(""); setThEndDate(""); setThCurrentMarket(false); }}
                                    className="agce-margin-filter-reset"
                                >Reset <i className="ri-refresh-line" /></button>

                                <div className="agce-margin-filter-checkbox-group">
                                    <label className="agce-margin-filter-checkbox-label">
                                        <input type="checkbox" checked={thCurrentMarket} onChange={(e) => setThCurrentMarket(e.target.checked)} />
                                        Current Market
                                    </label>
                                </div>
                            </FilterBarScrollable>

                            <div className="desktop_view2">
                                <div
                                    key={`spot-th-d-${spotHistoryViewportKey || "x"}`}
                                    className="table-responsive spot_orders_table_wrap"
                                    style={{ height: "310px", minHeight: "280px", overflowY: "auto" }}
                                    onScroll={onSpotTradeHistoryScrollViewport}
                                >
                                    {(() => {
                                        const rows = (myTrades || []).filter((item) => {
                                            if (thCurrentMarket && spotHistoryViewportKey && String(item?.pair || "") !== String(spotHistoryViewportKey)) return false;
                                            return true;
                                        });
                                        if (!rows.length) {
                                            return (
                                                <div className="spot_orders_empty_state" role="status">
                                                    <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                </div>
                                            );
                                        }
                                        return (
                                            <table className="table table_home spot_orders_table">
                                                <thead>
                                                    <tr>
                                                        <th>Markets</th>
                                                        <th>Side</th>
                                                        <th>Fill Price</th>
                                                        <th>Amount</th>
                                                        <th>Total</th>
                                                        <th>Time</th>
                                                        <th>Role</th>
                                                        <th>Fee</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rows.map((item, index) => {
                                                        const baseCc = tradeHistoryBaseAsset(item);
                                                        const quoteCc = tradeHistoryQuoteAsset(item);
                                                        const sideClass = spotSideColorClass(item?.side);
                                                        const dateStr = formatTradeHistoryDateTime(item, "YYYY-MM-DD");
                                                        const timeStr = formatTradeHistoryDateTime(item, "HH:mm:ss");
                                                        return (
                                                            <tr key={item?._id || item?.trade_id || index}>
                                                                <td>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>

                                                                        <span style={{ fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>{tradeHistoryMarketLabel(item)}</span>
                                                                    </div>
                                                                </td>
                                                                <td className={sideClass}>{item?.side || "—"}</td>
                                                                <td>
                                                                    {safeToFixed8(item?.price, "0")}
                                                                    {quoteCc ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{quoteCc}</span> : null}
                                                                </td>
                                                                <td>
                                                                    {safeToFixed8(item?.quantity, "0")}
                                                                    {baseCc ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{baseCc}</span> : null}
                                                                </td>
                                                                <td>
                                                                    {safeToFixed8(item?.quote_quantity, "0")}
                                                                    {quoteCc ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{quoteCc}</span> : null}
                                                                </td>
                                                                <td style={{ whiteSpace: "nowrap" }}>
                                                                    <div style={{ lineHeight: 1.3 }}>
                                                                        <div>{dateStr}</div>
                                                                        <small style={{ color: "#9ca3af" }}>{timeStr}</small>
                                                                    </div>
                                                                </td>
                                                                <td>{tradeHistoryRoleLabel(item)}</td>
                                                                <td>
                                                                    {safeToFixed8(item?.fee, "0")}
                                                                    {item?.fee_asset ? <span style={{ color: "#9ca3af", marginLeft: 3 }}>{item.fee_asset}</span> : null}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        );
                                    })()}
                                </div>
                            </div>

                            <div
                                key={`spot-th-m-${spotHistoryViewportKey || "x"}`}
                                className="order_history_mobile_view twomobile so-mobile"
                                style={{ maxHeight: "310px", overflowY: "auto" }}
                                onScroll={onSpotTradeHistoryScrollViewport}
                            >
                                <div className="d-flex datalist_mbl flex-column">
                                    {(() => {
                                        const rows = (myTrades || []).filter((item) => {
                                            if (thCurrentMarket && spotHistoryViewportKey && String(item?.pair || "") !== String(spotHistoryViewportKey)) return false;
                                            return true;
                                        });
                                        if (!rows.length) {
                                            return (
                                                <div className="no-data-wrapper w-100">
                                                    <div className="spot_orders_empty_state" role="status">
                                                        <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return rows.map((item, index) => {
                                            const baseCc = tradeHistoryBaseAsset(item);
                                            const quoteCc = tradeHistoryQuoteAsset(item);
                                            return (
                                                <OrderMobileCardShell
                                                    key={item?._id || item?.trade_id || `th-${index}`}
                                                    desktopColumnCount={TRADE_TRADE_HISTORY_DESKTOP_COLS}
                                                    popupTitle="Trade details"
                                                    heroPrimary={tradeHistoryMarketLabel(item)}
                                                    heroSecondary={mobileHeroSideTypeSubtitle(item?.side, tradeHistoryRoleLabel(item))}
                                                >
                                                    <div className="so-mobileCard__row">
                                                        <span>Side</span>
                                                        <span className={spotSideColorClass(item?.side)}>{item?.side || "—"}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Fill Price</span>
                                                        <span className="so-num">
                                                            {safeToFixed8(item?.price, "0")}
                                                            {quoteCc ? ` ${quoteCc}` : ""}
                                                        </span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Amount</span>
                                                        <span className="so-num">
                                                            {safeToFixed8(item?.quantity, "0")}
                                                            {baseCc ? ` ${baseCc}` : ""}
                                                        </span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Total</span>
                                                        <span className="so-num">
                                                            {safeToFixed8(item?.quote_quantity, "0")}
                                                            {quoteCc ? ` ${quoteCc}` : ""}
                                                        </span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Time</span>
                                                        <span>
                                                            {formatTradeHistoryDateTime(item, "YYYY-MM-DD")}{" "}
                                                            <small>{formatTradeHistoryDateTime(item, "HH:mm:ss")}</small>
                                                        </span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Role</span>
                                                        <span>{tradeHistoryRoleLabel(item)}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Fee</span>
                                                        <span className="so-num">
                                                            {safeToFixed8(item?.fee, "0")}
                                                            {item?.fee_asset ? ` ${item.fee_asset}` : ""}
                                                        </span>
                                                    </div>
                                                </OrderMobileCardShell>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}

                    {positionOrderTab === "loanManagement" && (() => {
                        const loans = Array.isArray(loanRows) ? loanRows : [];
                        // Synthetic loan object for a standalone Borrow (no existing loan)
                        const borrowOnlyLoan = { coin: loanBaseCoin, _openTab: "borrow" };
                        return (
                            <div className="cnt_table active">
                                {/* ── Borrow button ── */}
                                {/* <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 0 4px" }}>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        style={{ fontSize: 12, padding: "4px 16px" }}
                                        onClick={() => setRepayModalLoan(borrowOnlyLoan)}
                                    >
                                        Borrow
                                    </button>
                                </div> */}
                                {/* ── Desktop table ── */}
                                <div className="desktop_view2">
                                    <div className="table-responsive spot_orders_table_wrap" style={{ minHeight: "320px" }}>
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Coin</th>
                                                    <th>Contract</th>
                                                    <th>Hourly Rate / APR</th>
                                                    <th>Outstanding Loan</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loans.length === 0 ? (
                                                    <tr className="no-data-row spot_orders_no_data_row">
                                                        <td colSpan={5}>
                                                            <div className="spot_orders_empty_state" role="status">
                                                                <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : loans.map((loan, i) => (
                                                    <tr key={loan.loan_id || i}>
                                                        <td>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

                                                                <span>{loan.coin || "—"}</span>
                                                            </div>
                                                        </td>
                                                        <td>{loan.contract || "—"}</td>
                                                        <td>
                                                            {loan.hourly_rate_pct != null ? `${loan.hourly_rate_pct}%` : "—"}
                                                            {" / "}
                                                            {loan.apr_pct != null ? `${loan.apr_pct}%` : "—"}
                                                        </td>
                                                        <td>
                                                            {loan.outstanding != null
                                                                ? `${parseFloat(loan.outstanding).toFixed(8)} ${loan.coin || ""}`
                                                                : "—"}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm"
                                                                style={{ background: "#374151", color: "#fff", fontSize: 12, whiteSpace: "nowrap", padding: "4px 12px", borderRadius: 4, border: "none" }}
                                                                onClick={() => setRepayModalLoan({ ...loan, _openTab: "repay" })}
                                                            >
                                                                Repay
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* ── Mobile cards ── */}
                                <div className="order_history_mobile_view twomobile so-mobile loan-management-mobile-container">
                                    {loans.length === 0 ? (
                                        <div className="spot_orders_empty_state py-4" role="status">
                                            <img src="/images/no-data.svg" alt="" width={100} height={120} className="spot_orders_empty_telescope" />
                                        </div>
                                    ) : loans.map((loan, i) => (
                                        <div
                                            key={loan.loan_id || i}
                                            className="loan-management-card"
                                        >
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="d-flex align-items-center gap-2">
                                                    {loan.coin_icon && (
                                                        <img
                                                            src={loan.coin_icon}
                                                            alt={loan.coin}
                                                            width={22}
                                                            height={22}
                                                            className="rounded-circle"
                                                            onError={(e) => { e.target.style.display = "none"; }}
                                                        />
                                                    )}
                                                    <span className="loan-card-coin-name">{loan.coin || "—"}</span>
                                                    {loan.contract && (
                                                        <span className="loan-card-contract-name">{loan.contract}</span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="loan-card-repay-btn"
                                                    onClick={() => setRepayModalLoan(loan)}
                                                >
                                                    Repay
                                                </button>
                                            </div>
                                            <div className="d-flex justify-content-between loan-card-info-row mb-1">
                                                <span className="loan-card-label">Hourly Rate / APR</span>
                                                <span className="loan-card-value">
                                                    {loan.hourly_rate_pct != null ? `${loan.hourly_rate_pct}%` : "—"}
                                                    {" / "}
                                                    {loan.apr_pct != null ? `${loan.apr_pct}%` : "—"}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between loan-card-info-row">
                                                <span className="loan-card-label">Outstanding Loan</span>
                                                <span className="loan-card-value">
                                                    {loan.outstanding != null
                                                        ? `${parseFloat(loan.outstanding).toFixed(8)} ${loan.coin || ""}`
                                                        : "—"}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* ── Borrow / Repay Modal ── */}
                    {repayModalLoan && (
                        <LoanBorrowRepayModal
                            loan={repayModalLoan}
                            baseCoin={loanBaseCoin}
                            quoteCoin={loanQuoteCoin}
                            buyCoinBal={loanBuyCoinBal}
                            sellCoinBal={loanSellCoinBal}
                            initialTab={repayModalLoan._openTab || "repay"}
                            onClose={() => setRepayModalLoan(null)}
                            onSuccess={onLoanRefresh}
                        />
                    )}

                    {positionOrderTab === "orderHistory" && (() => {
                        const OH_TYPE_TABS = [
                            { id: "LIMIT_MARKET", label: "Limit | Market" },
                            { id: "CONDITIONAL", label: "Conditional" },
                            { id: "TP_SL", label: "TP/SL" },
                            { id: "TWAP", label: "TWAP" },
                            { id: "ICEBERG_PRO", label: "Iceberg Pro" },
                            { id: "LOOP_ORDER", label: "Loop Order" },
                            { id: "TRAILING_STOP", label: "Trailing Stop" },
                            { id: "POV", label: "Percentage of Volume (POV)" },
                        ];
                        const fmtOhTs = (ts) => {
                            if (!ts) return { date: "—", time: "" };
                            const d = new Date(ts);
                            if (isNaN(d.getTime())) return { date: "—", time: "" };
                            const p = (n) => String(n).padStart(2, "0");
                            return {
                                date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
                                time: `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`,
                            };
                        };
                        const OH_ORDER_TYPES = [
                            { id: "ALL", label: "All" },
                            { id: "LIMIT", label: "Limit" },
                            { id: "MARKET", label: "Market" },
                            { id: "STOP_LIMIT", label: "Stop Limit" },
                            { id: "STOP_MARKET", label: "Stop Market" },
                        ];
                        const ohRows = (pastOrders || []).filter((item) => {
                            if (ohHideCanceled) {
                                const s = String(item?.status || "").toUpperCase().replace("CANCELLED", "CANCELED");
                                if (s === "CANCELED") return false;
                            }
                            if (ohCurrentMarket && spotHistoryViewportKey && String(item?.pair || "") !== String(spotHistoryViewportKey)) return false;
                            if (ohTypeTab !== "ALL") {
                                const type = String(item?.type || item?.order_type || "").toUpperCase();
                                if (type !== ohTypeTab) return false;
                            }
                            return true;
                        });

                        return (
                            <div className="cnt_table active">
                                {/* ── Filter bar ── */}
                                <FilterBarScrollable>
                                    <CustomFilterDropdown
                                        options={[
                                            { value: "All", label: "All Sides" },
                                            { value: "BUY", label: "Buy" },
                                            { value: "SELL", label: "Sell" }
                                        ]}
                                        value={ohSideFilter}
                                        onChange={(val) => setOhSideFilter(val)}
                                        label="Side"
                                    />

                                    <CustomFilterDropdown
                                        options={[
                                            { value: "", label: "Time Range" },
                                            { value: "1D", label: "1D" },
                                            { value: "7D", label: "7D" },
                                            { value: "30D", label: "30D" },
                                            { value: "90D", label: "90D" }
                                        ]}
                                        value={ohPreset}
                                        onChange={(val) => applyOhPreset(val)}
                                        label="Time Range"
                                    />

                                    <MarginDateInput
                                        value={ohStartDate}
                                        placeholder="From"
                                        onChange={(e) => { setOhStartDate(e.target.value); setOhPreset(""); }}
                                    />
                                    <span className="agce-margin-filter-arrow">→</span>
                                    <MarginDateInput
                                        value={ohEndDate}
                                        placeholder="To"
                                        onChange={(e) => { setOhEndDate(e.target.value); setOhPreset(""); }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => { setOhSideFilter("All"); setOhPreset(""); setOhStartDate(""); setOhEndDate(""); setOhHideCanceled(false); setOhCurrentMarket(false); }}
                                        className="agce-margin-filter-reset"
                                    >Reset <i className="ri-refresh-line" /></button>

                                    <div className="agce-margin-filter-checkbox-group">
                                        <label className="agce-margin-filter-checkbox-label">
                                            <input type="checkbox" checked={ohCurrentMarket} onChange={(e) => setOhCurrentMarket(e.target.checked)} />
                                            Current Market
                                        </label>
                                        <label className="agce-margin-filter-checkbox-label">
                                            <input type="checkbox" checked={ohHideCanceled} onChange={(e) => setOhHideCanceled(e.target.checked)} />
                                            Hide the Canceled
                                        </label>
                                    </div>
                                </FilterBarScrollable>

                                {/* ── Order type filter chips ── */}
                                <div className="agce-margin-order-type-container">
                                    {OH_ORDER_TYPES.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => setOhTypeTab(t.id)}
                                            className={`agce-margin-order-type-btn ${ohTypeTab === t.id ? 'active' : ''}`}
                                        >{t.label}</button>
                                    ))}
                                </div>

                                {/* ── Desktop table ── */}
                                <div className="desktop_view2">
                                    <div
                                        key={`spot-oh-d-${spotHistoryViewportKey || "x"}`}
                                        className="table-responsive"
                                        style={{ height: "310px", overflowY: "auto" }}
                                        onScroll={onSpotOrderHistoryScrollViewport}
                                    >
                                        <table className="table table_home spot_orders_table">
                                            <thead>
                                                <tr>
                                                    <th>Market</th>
                                                    <th>Creation Time</th>
                                                    <th>End Time</th>
                                                    <th>Side</th>
                                                    <th>Fill Price</th>
                                                    <th>Price</th>
                                                    <th>Filled/Amount</th>
                                                    <th>Total Fee</th>
                                                    <th>Type</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ohRows.length > 0 ? ohRows.map((item, index) => {
                                                    const quoteCc = orderHistoryQuoteCurrency(item);
                                                    const [baseSym] = orderHistoryMarketLabel(item).split("/");
                                                    const created = fmtOhTs(item?.created_at || item?.createdAt);
                                                    const ended = fmtOhTs(item?.updated_at || item?.end_time || item?.completed_at);
                                                    const side = String(item?.side || "").toUpperCase();
                                                    const avgPx = orderHistoryAvgFillPrice(item, safeToFixed8, spotOpenOrderFilledQty);
                                                    const limitPx = orderHistoryLimitOrMarketPrice(item, safeToFixed8);
                                                    const isMarket = String(item?.type || item?.order_type || "").toUpperCase() === "MARKET";
                                                    const filledQty = safeToFixed8(spotOpenOrderFilledQty(item), "0");
                                                    const totalQty = safeToFixed8(item?.quantity, "0");
                                                    const fillPctStr = (() => {
                                                        const fp = item?.fill_percent;
                                                        if (fp == null || String(fp).trim() === "") return "0.00%";
                                                        const s = String(fp).trim();
                                                        return s.endsWith("%") ? s : `${s}%`;
                                                    })();
                                                    const tif = spotOpenOrderTifLabel(item);
                                                    const lifeStatus = spotOrderLifecycleStatus(item);
                                                    const stLabel = orderHistoryStatusLabel(lifeStatus);
                                                    const stClass = orderHistoryStatusClass(lifeStatus);
                                                    const aggregatedLegs = aggregateOrderHistoryExecutedLegs(item);
                                                    const hasLegs = item?.executed_prices?.length > 0;

                                                    return (
                                                        <React.Fragment key={item?._id || `oh-${index}`}>
                                                            <tr
                                                                onClick={() => setExpandedRowIndex(expandedRowIndex === index ? null : index)}
                                                                className="cursor-pointer"
                                                            >
                                                                {/* Market */}
                                                                <td>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                        {hasLegs && (
                                                                            <span style={{ fontSize: 10, lineHeight: 1, color: "#6b7280" }}>
                                                                                {expandedRowIndex === index ? "▾" : "▸"}
                                                                            </span>
                                                                        )}
                                                                        {/* <img
                                                                            src={coinIconSrc}
                                                                            alt=""
                                                                            width={20}
                                                                            height={20}
                                                                            style={{ borderRadius: "50%", flexShrink: 0 }}
                                                                            onError={(e) => { e.target.style.display = "none"; }}
                                                                        /> */}
                                                                        <div>
                                                                            <div style={{ fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>{orderHistoryMarketLabel(item)}</div>
                                                                            <div style={{ fontSize: 11, color: "#9ca3af" }}>Cross</div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                {/* Creation Time */}
                                                                <td>
                                                                    <div style={{ fontSize: 12 }}>{created.date}</div>
                                                                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{created.time}</div>
                                                                </td>
                                                                {/* End Time */}
                                                                <td>
                                                                    <div style={{ fontSize: 12 }}>{ended.date}</div>
                                                                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{ended.time}</div>
                                                                </td>
                                                                {/* Side */}
                                                                <td className={spotSideColorClass(item?.side)}>
                                                                    {side === "BUY" ? "Buy" : side === "SELL" ? "Sell" : side}
                                                                </td>
                                                                {/* Fill Price */}
                                                                <td style={{ whiteSpace: "nowrap" }}>
                                                                    {avgPx !== "—" ? `${avgPx} ${quoteCc}` : "—"}
                                                                </td>
                                                                {/* Price */}
                                                                <td style={{ whiteSpace: "nowrap" }}>
                                                                    {isMarket ? "Smart Market" : (limitPx !== "—" ? `${limitPx} ${quoteCc}` : "—")}
                                                                </td>
                                                                {/* Filled/Amount */}
                                                                <td>
                                                                    <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>{filledQty}/{totalQty}</div>
                                                                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{fillPctStr}</div>
                                                                </td>
                                                                {/* Total Fee */}
                                                                <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                                                                    {(() => {
                                                                        const raw = item?.total_fee;
                                                                        const v = raw?.$numberDecimal != null ? raw.$numberDecimal : raw;
                                                                        const n = parseFloat(v);
                                                                        if (!Number.isFinite(n) || n === 0) return "—";
                                                                        // Determine fee asset: prefer base for BUY, quote for SELL
                                                                        const feeAsset = item?.executions?.[0]?.fee_asset ?? (item?.side === "BUY" ? item?.base_asset : item?.quote_asset) ?? "";
                                                                        return `${n.toFixed(8).replace(/\.?0+$/, "")} ${feeAsset}`;
                                                                    })()}
                                                                </td>
                                                                {/* Type (TIF) */}
                                                                <td>{tif}</td>
                                                                {/* Status */}
                                                                <td className={`text-${stClass}`}>{stLabel}</td>
                                                            </tr>
                                                            {expandedRowIndex === index && hasLegs && (
                                                                <tr>
                                                                    <td colSpan={10}>
                                                                        <div className="table-responsive" style={{ background: "rgba(0,0,0,0.04)", padding: "6px 12px", borderRadius: 6 }}>
                                                                            <table className="table table_home" style={{ marginBottom: 0 }}>
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>#</th>
                                                                                        <th>Price</th>
                                                                                        <th>Executed</th>
                                                                                        <th>Fee</th>
                                                                                        <th>Total</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {aggregatedLegs.map((leg, i) => (
                                                                                        <tr key={leg.reactKey}>
                                                                                            <td>{i + 1}</td>
                                                                                            <td>{safeToFixed8(leg.price, "0")} {quoteCc}</td>
                                                                                            <td>{safeToFixed8(leg.quantity, "0")} {baseSym}</td>
                                                                                            <td>{safeToFixed8(leg.fee, "0")} {quoteCc}</td>
                                                                                            <td>{safeToFixed8(Number(leg.price) * Number(leg.quantity), "0")}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                }) : (
                                                    <tr className="no-data-row spot_orders_no_data_row">
                                                        <td colSpan={9}>
                                                            <div className="spot_orders_empty_state" role="status">
                                                                <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* ── Mobile cards ── */}
                                <div
                                    key={`spot-oh-m-${spotHistoryViewportKey || "x"}`}
                                    className="mobile_view2"
                                    style={{ maxHeight: "353px", overflowY: "auto" }}
                                    onScroll={positionOrderTab === "orderHistory" ? onSpotOrderHistoryScrollViewport : undefined}
                                >
                                    {ohRows.length > 0 ? ohRows.map((item, index) => {
                                        const quoteCc = orderHistoryQuoteCurrency(item);
                                        const [baseSym] = orderHistoryMarketLabel(item).split("/");
                                        const created = fmtOhTs(item?.created_at || item?.createdAt);
                                        const ended = fmtOhTs(item?.updated_at || item?.end_time || item?.completed_at);
                                        const side = String(item?.side || "").toUpperCase();
                                        const avgPx = orderHistoryAvgFillPrice(item, safeToFixed8, spotOpenOrderFilledQty);
                                        const limitPx = orderHistoryLimitOrMarketPrice(item, safeToFixed8);
                                        const isMarket = String(item?.type || item?.order_type || "").toUpperCase() === "MARKET";
                                        const fillPctStr = (() => { const fp = item?.fill_percent; if (fp == null || String(fp).trim() === "") return "0.00%"; const s = String(fp).trim(); return s.endsWith("%") ? s : `${s}%`; })();
                                        const aggregatedLegs = aggregateOrderHistoryExecutedLegs(item);
                                        const mobLifeStatus = spotOrderLifecycleStatus(item);
                                        return (
                                            <React.Fragment key={item?._id || index}>
                                                <OrderMobileCardShell
                                                    desktopColumnCount={10}
                                                    popupTitle="Order details"
                                                    heroPrimary={orderHistoryMarketLabel(item)}
                                                    heroSecondary={mobileHeroSideTypeSubtitle(item?.side, spotOpenOrderTifLabel(item))}
                                                >
                                                    <div className="so-mobileCard__row"><span>Creation Time</span><span>{created.date} {created.time}</span></div>
                                                    <div className="so-mobileCard__row"><span>Fill Price</span><span>{avgPx !== "—" ? `${avgPx} ${quoteCc}` : "—"}</span></div>
                                                    <div className="so-mobileCard__row"><span>Price</span><span>{isMarket ? "Smart Market" : (limitPx !== "—" ? `${limitPx} ${quoteCc}` : "—")}</span></div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Filled/Amount</span>
                                                        <span>{safeToFixed8(spotOpenOrderFilledQty(item), "0")}/{safeToFixed8(item?.quantity, "0")} · {fillPctStr}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Total Fee</span>
                                                        <span>{(() => {
                                                            const raw = item?.total_fee;
                                                            const v = raw?.$numberDecimal != null ? raw.$numberDecimal : raw;
                                                            const n = parseFloat(v);
                                                            if (!Number.isFinite(n) || n === 0) return "—";
                                                            const feeAsset = item?.executions?.[0]?.fee_asset ?? (item?.side === "BUY" ? item?.base_asset : item?.quote_asset) ?? "";
                                                            return `${n.toFixed(8).replace(/\.?0+$/, "")} ${feeAsset}`;
                                                        })()}</span>
                                                    </div>
                                                    <div className="so-mobileCard__row">
                                                        <span>Status</span>
                                                        <span className={`text-${orderHistoryStatusClass(mobLifeStatus)}`}>{orderHistoryStatusLabel(mobLifeStatus)}</span>
                                                    </div>
                                                </OrderMobileCardShell>
                                                {item?.executed_prices?.length > 0 && (
                                                    <div className="order_datalist trade_page_mob_execBelow">
                                                        <div className={`executed_trades_list ${showExecutedTrades[item?._id] ? "active" : ""}`}>
                                                            <button type="button" onClick={() => setShowExecutedTrades({ ...showExecutedTrades, [item?._id]: !showExecutedTrades[item?._id] })}>
                                                                <i className={`ri-arrow-drop-down-line ${showExecutedTrades[item?._id] ? "rotated" : ""}`} /> Executed trades
                                                            </button>
                                                            {showExecutedTrades[item?._id] && (
                                                                <div className="executed_trades_list_items">
                                                                    {aggregatedLegs.map((leg, i) => (
                                                                        <ul key={leg.reactKey}>
                                                                            <li>Trade #{i + 1}:</li>
                                                                            <li>Price: <span>{safeToFixed8(leg.price, "0")} {quoteCc}</span></li>
                                                                            <li>Executed: <span>{safeToFixed8(leg.quantity, "0")} {baseSym}</span></li>
                                                                            <li>Fee: <span>{safeToFixed8(leg.fee, "0")} {quoteCc}</span></li>
                                                                            <li>Total: <span>{safeToFixed8(Number(leg.price) * Number(leg.quantity), "0")}</span></li>
                                                                        </ul>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    }) : (
                                        <div className="no-data-wrapper w-100 d-flex justify-content-center py-4">
                                            <div className="spot_orders_empty_state" role="status">
                                                <img src="/images/no-data.svg" alt="" width={120} height={144} className="spot_orders_empty_telescope" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {positionOrderTab === "assetHistory" && (
                        <AssetHistoryTab
                            pairId={SelectedCoin?._id}
                            token={token}
                        />
                    )}

                </div>

                <div className="assets_right d-none d-lg-block assets_panel_desktop">
                    <div id="tab_4" className="assets_panel_inner">
                        <div className="assets_panel_header">
                            <div>
                                <h5 className="assets_panel_title">Assets</h5>
                                <span style={{ fontSize: 11, color: "#8E949E", display: "block", marginTop: 2 }}>
                                    Cross · Shared account
                                </span>
                            </div>
                            <button type="button" className="assets_panel_refresh" onClick={() => fetchSpotWallets()} aria-label="Refresh balances">
                                <i className="ri-refresh-line" />
                            </button>
                        </div>

                        <div className="assets_balance_rows">
                            <div className="assets_balance_row">
                                <span className="assets_balance_label">{SelectedCoin?.quote_currency || "USDT"} Balance</span>
                                <span className="assets_balance_val">
                                    {token
                                        ? `${BuyCoinBal !== undefined && BuyCoinBal !== null ? parseFloat(BuyCoinBal).toFixed(8).replace(/\.?0+$/, "") : "0"} ${SelectedCoin?.quote_currency || "USDT"}`
                                        : `-- ${SelectedCoin?.quote_currency || "USDT"}`}
                                </span>
                            </div>
                            <div className="assets_balance_row">
                                <span className="assets_balance_label">{SelectedCoin?.base_currency || "BTC"} Balance</span>
                                <span className="assets_balance_val">
                                    {token
                                        ? `${SellCoinBal !== undefined && SellCoinBal !== null ? parseFloat(SellCoinBal).toFixed(8).replace(/\.?0+$/, "") : "0"} ${SelectedCoin?.base_currency || "BTC"}`
                                        : `-- ${SelectedCoin?.base_currency || "BTC"}`}
                                </span>
                            </div>
                        </div>

                        <div className="assets_panel_actions">
                        <Link className="assets_panel_pill" to={token ? "/user_profile/wallet/cross_margin" : "/login"}>
                                Account
                            </Link>
                            <Link className="assets_panel_pill" to={token ? "/asset_managemnet/deposit" : "/login"}>
                                Deposit
                            </Link>
                           
                            <Link className="assets_panel_pill" to={token ? "/user_profile/asset_overview" : "/login"}>
                                Transfer
                            </Link>
                        </div>

                        <div className="assets_panel_list_card">
                            {!token ? (
                                <div className="assets_panel_login_hint">
                                    <p className="assets_panel_login_text mb-2">Please login to view your margin account</p>
                                    <Link to="/login" className="btn btn-primary btn-sm">Login</Link>
                                </div>
                            ) : (() => {
                                const fmt = (v) => parseFloat((parseFloat(v) || 0).toFixed(8));
                                const mb = marginBalance || {};
                                const quoteAvail = fmt(BuyCoinBal);
                                const quoteLocked = fmt(mb.quoteLocked);
                                const quoteBorrowed = fmt(mb.quoteBorrowed);
                                const baseAvail = fmt(SellCoinBal);
                                const baseLocked = fmt(mb.baseLocked);
                                const baseBorrowed = fmt(mb.baseBorrowed);
                                const quoteCoin = SelectedCoin?.quote_currency || "USDT";
                                const baseCoin  = SelectedCoin?.base_currency  || "BTC";
                                const pairLabel = SelectedCoin ? `${baseCoin}/${quoteCoin}` : null;
                                const rowStyle  = { display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0", color: "#8E949E" };
                                const valStyle  = { color: "#000" };
                                return (
                                    <ul className="assets_panel_asset_rows" style={{ gap: 12 }}>
                                        {/* Pair header */}
                                        {pairLabel && (
                                            <li style={{ listStyle: "none", padding: "4px 0 6px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                    <span style={{ fontSize: 13, fontWeight: 700 }}>{pairLabel}</span>
                                                    <span style={{ fontSize: 10, color: "#8E949E", background: "rgba(142,148,158,0.12)", borderRadius: 3, padding: "1px 6px" }}>
                                                        Cross
                                                    </span>
                                                </div>
                                                <div className="d-flex flex-column align-items-center assets_panel_margin_level" style={{ marginTop: 10 }}>
                                                    <div className="cmp-margin-gauge-label d-flex align-items-center gap-1">
                                                        Margin Level
                                                        <button
                                                            type="button"
                                                            className="agce-margin-level-info-btn"
                                                            onClick={() => setCrossRiskModalOpen(true)}
                                                            aria-label="View margin risk details"
                                                        >
                                                            <i className="ri-information-line agce-margin-level-info-icon" aria-hidden />
                                                        </button>
                                                    </div>
                                                    <CrossMarginLevelGauge
                                                        level={parsedCrossRisk.margin_level}
                                                        mmr={parsedCrossRisk.liquidationMarginLevel ?? 1.1}
                                                        warningRate={parsedCrossRisk.marginCallLevel ?? 1.15}
                                                    />
                                                </div>
                                            </li>
                                        )}
                                        {[
                                            { coin: quoteCoin, avail: quoteAvail, locked: quoteLocked, borrowed: quoteBorrowed },
                                            { coin: baseCoin,  avail: baseAvail,  locked: baseLocked,  borrowed: baseBorrowed  },
                                        ].map(({ coin, avail, locked, borrowed }) => (
                                            <li key={coin} className="assets_panel_asset_row" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: "#000", marginBottom: 2 }}>{coin}</span>
                                                <div style={rowStyle}><span>Available</span><span style={valStyle}>{avail}</span></div>
                                                <div style={rowStyle}><span>In Order</span><span style={valStyle}>{locked}</span></div>
                                                <div style={rowStyle}><span>Borrowed</span><span style={{ color: borrowed > 0 ? "#e45561" : "#8E949E" }}>{borrowed}</span></div>
                                            </li>
                                        ))}
                                    </ul>
                                );
                            })()}
                        </div>
                    </div>
                </div>

            </div>

            {closePositionModal && (() => {
                // closePositionModal is the pair string (e.g. "BTCUSDT") or built from asset
                const positions = Array.isArray(marginPositionData) ? marginPositionData : (marginPositionData ? [marginPositionData] : []);
                const pos = typeof closePositionModal === "string"
                    ? (positions.find((p) => (p.pair || `${p.asset}USDT`) === closePositionModal)
                        ?? positions.find((p) => closePositionModal.startsWith(p.asset || ""))
                        ?? positions[0] ?? null)
                    : positions[0] ?? null;
                const baseAsset  = pos?.pair ? pos.pair.slice(0, -4) : (pos?.asset || SelectedCoin?.base_currency || "");
                const quoteAsset = pos?.pair ? pos.pair.slice(-4)    : (pos?.quote_asset || SelectedCoin?.quote_currency || "USDT");
                const pairLabel  = pos?.pair
                    ? `${pos.pair.slice(0, -4)}/${pos.pair.slice(-4)}`
                    : (pos?.asset ? `${pos.asset}/${quoteAsset}` : (SelectedCoin ? `${SelectedCoin.base_currency}/${SelectedCoin.quote_currency}` : "—"));
                const maxQty = parseFloat(pos?.quantity || 0);
                const notional = parseFloat(pos?.notional || 0);
                const sideLabel = pos?.side === "LONG" ? "Long" : pos?.side === "SHORT" ? "Short" : "—";

                return (
                    <>
                        <div className="modal-backdrop fade show" onClick={() => setClosePositionModal(false)} />
                        <div
                            className="modal fade kyc_modal show d-block"
                            tabIndex="-1"
                            role="dialog"
                            aria-modal="true"
                            style={{ zIndex: 1055 }}
                        >
                            <div className="modal-dialog modal-dialog-centered close-position-modal-dialog" role="document">
                                <div className="modal-content close-position-modal-content">

                                    <div className="modal-header border-0 pb-1 px-4 pt-4">
                                        <h5 className="modal-title fw-bold close-position-modal-title">
                                            Close Position
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={() => setClosePositionModal(false)}
                                        />
                                    </div>

                                    <div className="modal-body px-4 pt-2 pb-0">
                                        <div className="d-flex align-items-center gap-2 mb-3 close-position-modal-subtitle">
                                            <span className="fw-semibold text-body">{pairLabel}</span>
                                            <span>·</span>
                                            <span>Cross</span>
                                            <span>·</span>
                                            <span className={`fw-bold ${pos?.side === "LONG" ? "close-position-modal-side-long" : "close-position-modal-side-short"}`}>
                                                {sideLabel}
                                            </span>
                                        </div>

                                        <div className="close-position-settle-card mb-3">
                                            <div className="fw-semibold mb-1">Settle the position</div>
                                            <div className="close-position-settle-card-desc">
                                                Your full holding will be closed as a market order. Remaining assets stay in your Cross Margin account.
                                            </div>
                                        </div>

                                        <div className="mb-3 close-position-info-section">
                                            {[
                                                { label: "Holding", value: `${maxQty || "—"} ${baseAsset}` },
                                                { label: "Notional", value: notional ? `${notional.toFixed(4)} ${quoteAsset}` : "—" },
                                            ].map(({ label, value }) => (
                                                <div key={label} className="d-flex justify-content-between align-items-center py-1 close-position-info-row">
                                                    <span className="close-position-info-label">{label}</span>
                                                    <span className="close-position-info-value">{value}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <p className="close-position-body-text mb-2">
                                            Using Close Position for large sizes may result in losses due to market slippage. Only trading fees are charged.
                                        </p>
                                        <p className="close-position-body-text-muted mb-3">
                                            Amounts received may differ due to the nature of market orders. Larger orders may experience higher slippage. Submit another request if your position is not fully settled.
                                        </p>
                                    </div>

                                    <div className="modal-footer border-0 px-4 pb-4 pt-2">
                                        <button
                                            type="button"
                                            className="btn w-100 close-position-confirm-btn"
                                            onClick={handleClosePosition}
                                            disabled={closePositionLoading}
                                        >
                                            {closePositionLoading ? (
                                                <>
                                                    <ThemeAwareSpinner as="span" size="sm" className="me-2" aria-hidden="true" />
                                                    Closing…
                                                </>
                                            ) : "Confirm"}
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </>
                );
            })()}
            {crossRiskModalOpen && (
                <CrossMarginRiskModal risk={crossRisk} onClose={() => setCrossRiskModalOpen(false)} />
            )}
        </>
    );
};

export default memo(TradeHistorySection);
